import React, { useState, useEffect, useCallback, useRef } from 'react';
import { authFetchInit, getAuthHeaders, hasAuthSession } from '../utils/authSession';
import { listingTableSx } from '../components/listingTableSx';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Divider,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Badge,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Avatar,
  Stack,
  Fade,
  LinearProgress,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  People as PeopleIcon,
  Launch as LaunchIcon,
  Security as SecurityIcon,
  AccountCircle as AccountCircleIcon,
  VpnKey as VpnKeyIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Schedule,
  PlayArrow,
  CheckCircleOutline,
  Warning,
  Apps as AppsIcon,
  Star,
  HelpOutline,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  InfoOutlined as InfoOutlinedIcon,
  HelpOutline as HelpOutlineIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  FilterList as FilterListIcon,
  Dashboard as DashboardIcon,
  Cancel as CancelIcon,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon,
  Lock as LockIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  RateReview as RateReviewIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Description as FormIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { applicationsAPI, usersAPI, ssoAPI, oktaAPI } from '../services/api';
import { instancesAPI } from '../services/instancesAPI';
import delegationAPI from '../services/delegationAPI';
import {
  calendarDay,
  delegationAllows,
  delegationIsCurrent,
  delegationValidationError,
  DELEGATION_SCOPES,
} from '../utils/delegationAccess';
import { Application, User, CreateApplicationRequest, ApplicationDelegate, AppInstance } from '../types';
import SSOOnboardingDialog from '../components/SSOOnboardingDialog';
import OwnersDialog from '../components/OwnersDialog';
import SSODetailsSmallDialog from '../components/SSODetailsSmallDialog';
import InstanceEditorDialog from '../components/InstanceEditorDialog';
import SSOConfigCompactDetails from '../components/SSOConfigCompactDetails';
import { SSOProgressStepper } from '../components/SSOStatusWidgets';
import RenewClientSecretControl, { formatSecretExpiry } from '../components/RenewClientSecretControl';
import OIDCCredentialsDialog from '../components/OIDCCredentialsDialog';
import { API_ROOT } from '../config/apiConfig';
import { getApiErrorMessage } from '../utils/apiErrors';
import {
  buildAzureFederationMetadataUrl,
  buildAzureIdpInitiatedUrl,
  buildAzureOidcDiscoveryUrl,
} from '../constants/urlTemplates';
import { UI_TOKENS } from '../constants/uiTokens';
import { ListingPagination, ListingSortSelect } from '../components/ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';
import CenteredLoader from '../components/CenteredLoader';
import { getEnvironmentSurface, getProtocolSurface, getRiskSurface, getStatusSurface, chipSxFromSurface } from '../utils/appearanceTokens';

// Using AppInstance interface from types

function ownerControlPreview(assessment: any): { score: number | null; name: string; sentence: string } | null {
  if (!assessment) return null;
  let controls = assessment.recommended_controls;
  if (typeof controls === 'string') {
    try {
      controls = JSON.parse(controls);
    } catch {
      controls = null;
    }
  }
  if (!Array.isArray(controls) || controls.length === 0) return null;
  const first = controls.find((control: any) => control?.owner_sentence) || controls[0];
  if (!first?.control_name && !first?.owner_sentence) return null;
  const score = assessment.calculated_risk_score;
  return {
    score: typeof score === 'number' ? score : score == null || score === '' ? null : Number(score),
    name: first.control_name || '',
    sentence: first.owner_sentence || first.control_description || '',
  };
}

const Applications: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showInfo } = useNotification();
  const { colors, resolvedMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Determine the current onboarding type based on the route
  const getCurrentOnboardingType = () => {
    if (location.pathname.includes('/sso-onboarding')) return 'sso';
    if (location.pathname.includes('/iga-onboarding')) return 'iga';
    if (location.pathname.includes('/pam-dashboard')) return 'pam';
    return 'all';
  };
  
  const currentOnboardingType = getCurrentOnboardingType();
  
  // Filter applications based on onboarding type
  const getFilteredApplicationsByType = (apps: Application[]) => {
    if (currentOnboardingType === 'all') return apps;
    
    return apps.filter(app => {
      const instances = applicationInstances[app.id] || [];
      return instances.some(instance => {
        if (currentOnboardingType === 'sso') {
          return instance.sso_status === 'Not Started' || instance.sso_status === 'Pending Review';
        } else if (currentOnboardingType === 'iga') {
          return instance.iga_status === 'Not Started' || instance.iga_status === 'Pending Review';
        } else if (currentOnboardingType === 'pam') {
          return instance.pam_status === 'Not Started' || instance.pam_status === 'Pending Review';
        }
        return true;
      });
    });
  };
  const [applications, setApplications] = useState<Application[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [applicationInstances, setApplicationInstances] = useState<{[appId: string]: AppInstance[]}>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [setupStep, setSetupStep] = useState(0);
  const [setupProtocol, setSetupProtocol] = useState('SAML');
  const [setupEnvironment, setSetupEnvironment] = useState('Production');
  const [cardMenu, setCardMenu] = useState<{ anchor: HTMLElement; appId: string } | null>(null);
  const [editingApplication, setEditingApplication] = useState<Application | null>(null);
  const [activeTab, setActiveTab] = useState<string>(() => {
    const tab = searchParams.get('tab');
    if (tab) return tab;
    
    // Use route-based tab setting as fallback
    if (currentOnboardingType === 'sso') return 'sso';
    if (currentOnboardingType === 'iga') return 'iga';
    if (currentOnboardingType === 'pam') return 'pam';
    return 'all';
  });


  const [ssoDialogOpen, setSsoDialogOpen] = useState(false);
  const [selectedAppForSSO, setSelectedAppForSSO] = useState<Application | null>(null);
  const [selectedInstanceForSSO, setSelectedInstanceForSSO] = useState<string>(''); // Track which instance
  const [instanceDialogOpen, setInstanceDialogOpen] = useState(false);
  const [editingInstance, setEditingInstance] = useState<AppInstance | null>(null);
  const [selectedAppForInstance, setSelectedAppForInstance] = useState<Application | null>(null);
  const [instanceFormData, setInstanceFormData] = useState({
    name: '',
    environment: 'Production',
    description: ''
  });
  const [ownersDialogOpen, setOwnersDialogOpen] = useState(false);
  const [selectedAppForOwners, setSelectedAppForOwners] = useState<Application | null>(null);
  const [ssoDetailsSmallDialogOpen, setSsoDetailsSmallDialogOpen] = useState(false);
  const [selectedAppForSSODetails, setSelectedAppForSSODetails] = useState<Application | null>(null);
  const [oidcDialogOpen, setOidcDialogOpen] = useState(false);
  const [oidcCredentials, setOidcCredentials] = useState<{
    clientId: string;
    clientSecret?: string;
    oidcDiscoveryEndpoint: string;
    clientSecretExpiresAt?: string;
    grantType: 'authorization_code' | 'client_credentials' | 'authorization_code_pkce';
    connectionName: string;
  } | null>(null);

  const [viewSSODialogOpen, setViewSSODialogOpen] = useState(false);
  const [selectedSSOConfig, setSelectedSSOConfig] = useState<any>(null);
  const [isEditingSSO, setIsEditingSSO] = useState(false);
  const [ssoEditFormData, setSsoEditFormData] = useState<any>({
    sso_type: 'SAML',
    grant_type: 'authorization_code_pkce',
    entity_id: '',
    acs_url: '',
    name_id_format: '',
    x509_certificate: '',
    private_key: '',
    client_id: '',
    client_secret: '',
    redirect_uris: [],
    scopes: [],
    connection_name: '',
    contact_email: '',
    metadata_url: '',
    metadata_xml: '',
    attributes_to_release: []
  });
  const [ssoNotes, setSsoNotes] = useState<any[]>([]);
  const [newNote, setNewNote] = useState('');
  
  // Attributes state for SSO edit dialog
  const [availableAttributes, setAvailableAttributes] = useState<any[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
  
  // Notes timeline dialog state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedAppForNotes, setSelectedAppForNotes] = useState<Application | null>(null);
  
  // Delegation dialog state
  const [delegationDialogOpen, setDelegationDialogOpen] = useState(false);
  const [selectedAppForDelegation, setSelectedAppForDelegation] = useState<Application | null>(null);
  const [delegationLoading, setDelegationLoading] = useState(false);
  const [delegationFormData, setDelegationFormData] = useState({
    delegateTo: '',
    businessJustification: ''
  });

  // Application delegates management state
  const [delegatesDialogOpen, setDelegatesDialogOpen] = useState(false);
  const [selectedAppForDelegates, setSelectedAppForDelegates] = useState<Application | null>(null);
  const [applicationDelegates, setApplicationDelegates] = useState<ApplicationDelegate[]>([]);
  const emptyDelegateForm = {
    userIds: [] as string[],
    businessJustification: '',
    startsAt: '',
    endsAt: '',
    scopes: ['view'] as string[],
  };
  const [newDelegateFormData, setNewDelegateFormData] = useState(emptyDelegateForm);

  // Reporting state
  const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
  const [inactiveUsersReport, setInactiveUsersReport] = useState<any[]>([]);
  const [allInactiveReport, setAllInactiveReport] = useState<any[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedInstanceForNotes, setSelectedInstanceForNotes] = useState<string>('');
  const [instanceNotes, setInstanceNotes] = useState<any[]>([]);
  
  // Notification state
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotification, setShowNotification] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Main applications pagination state
  const [mainCurrentPage, setMainCurrentPage] = useState(1);
  const [mainItemsPerPage, setMainItemsPerPage] = useState(10);
  
  // Track created instances for each application (no longer using localStorage)
  const [createdInstances, setCreatedInstances] = useState<{[appId: string]: AppInstance[]}>({});

  // Form states
  const [formData, setFormData] = useState<CreateApplicationRequest>({
    name: '',
    description: '',
    application_id: '',
    business_function: '',
    risk_level: 'Not Defined',
    status: 'Active',
    primary_owner_ids: [] as string[],
    secondary_owner_ids: [] as string[]
  });

  // Risk Assessment state
  const [appAssessments, setAppAssessments] = useState<Record<string, any>>({});
  const [raDialogOpen, setRaDialogOpen] = useState(false);
  const [raApp, setRaApp] = useState<Application | null>(null);
  const [raQuestions, setRaQuestions] = useState<any[]>([]);
  const [raAnswers, setRaAnswers] = useState<Record<string, any>>({});
  const [raAssessmentId, setRaAssessmentId] = useState<string | null>(null);
  const [raLoading, setRaLoading] = useState(false);
  const [raSubmitting, setRaSubmitting] = useState(false);
  const [raActiveTab, setRaActiveTab] = useState(0);
  const [raReviewNotes, setRaReviewNotes] = useState<string>('');
  const [raError, setRaError] = useState<string | null>(null);
  const [raAssessmentStatus, setRaAssessmentStatus] = useState<string | null>(null);
  const [raControlPreview, setRaControlPreview] = useState<{ score: number | null; name: string; sentence: string } | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [showDemoRows, setShowDemoRows] = useState(() => {
    try {
      return localStorage.getItem('provysn-show-demo-rows') === '1';
    } catch {
      return false;
    }
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [platformStatusFilter, setPlatformStatusFilter] = useState('all');
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  const [platformListingSort, setPlatformListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  
  // Platform-specific filter states
  const [platformSearchTerm, setPlatformSearchTerm] = useState('');
  const [platformEnvironmentFilter, setPlatformEnvironmentFilter] = useState('all');
  const [platformTabStatusFilter, setPlatformTabStatusFilter] = useState('all');
  const [platformRiskFilter, setPlatformRiskFilter] = useState('all');
  const [platformViewMode, setPlatformViewMode] = useState<'table' | 'card'>('table');
  const [ssoStatistics, setSsoStatistics] = useState<any>(null);
  const [ssoConfigurations, setSsoConfigurations] = useState<any[]>([]);
  
  // Calculate status counts from actual SSO configurations
  const getStatusCounts = () => {
    const counts = {
      'Pending Review': 0,
      'In Progress': 0,
      'Approved': 0,
      'Completed': 0,
      'Rejected': 0,
      'Need More Info': 0
    };
    
    ssoConfigurations.forEach(config => {
      if (counts.hasOwnProperty(config.status)) {
        counts[config.status as keyof typeof counts]++;
      }
    });
    return counts;
  };

  // Calculate comprehensive statistics
  const getComprehensiveStats = () => {
    const totalApplications = applications.length;
    const totalInstances = applications.reduce((total, app) => total + getInstances(app).length, 0);
    const totalSSOOnboarded = getStatusCounts()['Approved'] + getStatusCounts()['Completed'];
    const totalPending = getStatusCounts()['Pending Review'] + getStatusCounts()['Need More Info'];
    const totalInProgress = getStatusCounts()['In Progress'];
    const totalRejected = getStatusCounts()['Rejected'];

    // Calculate IGA and PAM onboarded counts
    // For now, these are 0 since IGA and PAM onboarding is not implemented yet
    const totalIGAOnboarded = 0; // Will be implemented when IGA onboarding is added
    const totalPAMOnboarded = 0; // Will be implemented when PAM onboarding is added

    return {
      totalApplications,
      totalInstances,
      totalSSOOnboarded,
      totalIGAOnboarded,
      totalPAMOnboarded,
      totalPending,
      totalInProgress,
      totalRejected
    };
  };
  
  // Unread comment tracking
  const [unreadComments, setUnreadComments] = useState<{[instanceId: string]: number}>({});
  const [lastViewedComments, setLastViewedComments] = useState<{[instanceId: string]: string}>({});
  
  // Import functionality state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  
  // Import IDP dialog state
  const [importIdpDialogOpen, setImportIdpDialogOpen] = useState(false);
  const [importIdpError, setImportIdpError] = useState<string | null>(null);
  const [configuredImportIdps, setConfiguredImportIdps] = useState<Array<{ id: string; label: string }>>([]);
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedIdp, setSelectedIdp] = useState<string>('');
  const [availableApplications, setAvailableApplications] = useState<any[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [importIdpLoading, setImportIdpLoading] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const isSSOAdmin = user?.role === 'sso_admin';
  const isIGAAdmin = user?.role === 'iga_admin';
  const isPAMAdmin = user?.role === 'pam_admin';
  const isGrcAdmin = user?.role === 'grc_admin';
  const isAuditor = user?.role === 'auditor';
  const isEnterprisePortfolioViewer =
    isSuperAdmin || isIGAAdmin || isPAMAdmin || isGrcAdmin || isAuditor;
  const isAdmin = isSuperAdmin;
  const isAppOwner = user?.role === 'app_admin';
  const isDelegate = user?.role === 'delegate';
  
  console.log('🔍 User role debugging:', {
    user: user,
    userRole: user?.role,
    isSuperAdmin: isSuperAdmin,
    isSSOAdmin: isSSOAdmin,
    isAdmin: isAdmin
  });
  
  // Test alert to see if component is loading
  if (location.pathname.includes('/sso-onboarding')) {
    console.log('🚨 SSO Onboarding page loaded!');
  }

  // Helper function to check if search term matches any field
  const getSearchMatchInfo = (app: Application) => {
    if (!searchTerm) return { hasMatch: false, matchedFields: [] };
    
    const searchLower = searchTerm.toLowerCase();
    const matchedFields: string[] = [];
    
    // Check app details
    if (app.name.toLowerCase().includes(searchLower)) matchedFields.push('name');
    if (app.description?.toLowerCase().includes(searchLower)) matchedFields.push('description');
    if (app.business_function?.toLowerCase().includes(searchLower)) matchedFields.push('business_function');
    
    // Check primary owners
    if (app.primary_owners?.some(owner => 
      owner.first_name?.toLowerCase().includes(searchLower) ||
      owner.last_name?.toLowerCase().includes(searchLower) ||
      owner.email?.toLowerCase().includes(searchLower) ||
      owner.username?.toLowerCase().includes(searchLower)
    )) matchedFields.push('primary_owner');
    
    // Check secondary owners
    if (app.secondary_owners?.some(owner => 
      owner.first_name?.toLowerCase().includes(searchLower) ||
      owner.last_name?.toLowerCase().includes(searchLower) ||
      owner.email?.toLowerCase().includes(searchLower) ||
      owner.username?.toLowerCase().includes(searchLower)
    )) matchedFields.push('secondary_owner');
    
    // Check delegates
    if (getInstances(app).some(instance => 
      instance.delegatedTo?.toLowerCase().includes(searchLower) ||
      instance.assignedTo?.toLowerCase().includes(searchLower)
    )) matchedFields.push('delegate');
    
    return { hasMatch: matchedFields.length > 0, matchedFields };
  };

  // Filter applications based on search and filter criteria
  const getFilteredApplications = () => {
    // First apply onboarding type filtering
    const typeFilteredApps = getFilteredApplicationsByType(applications);
    
    return typeFilteredApps.filter(app => {
      // Role-based filtering - only show applications user has access to
      if (isEnterprisePortfolioViewer) {
        // Platform admins see the enterprise portfolio returned by the API
      } else if (user?.role === 'app_admin' || user?.role === 'sso_admin') {
        // App owners and SSO admins see only applications they own
        if (!isApplicationOwner(app)) {
          return false;
        }
      } else if (user?.role === 'delegate') {
        // Delegates can only see applications where they are assigned as delegates
        if (!isApplicationDelegate(app)) {
          return false;
        }
      } else if (user?.role === 'user') {
        // Standard users: list is already scoped by the API (owners, delegates, instance assignment, SSO assignee)
      } else if (user?.role === 'auditor') {
        // Auditors see the portfolio returned by the API
      } else {
        // Fallback: require application-level owner or delegate
        if (!isApplicationOwner(app) && !isApplicationDelegate(app)) {
          return false;
        }
      }

      // Search term filter - search across name, description, business function, and owners
      const searchLower = searchTerm.toLowerCase();
      
      // Check if search term matches app details
      const matchesAppDetails = app.name.toLowerCase().includes(searchLower) ||
        (app.description && app.description.toLowerCase().includes(searchLower)) ||
        (app.business_function && app.business_function.toLowerCase().includes(searchLower));
      
      // Check if search term matches primary owners
      const matchesPrimaryOwners = app.primary_owners?.some(owner => 
        owner.first_name?.toLowerCase().includes(searchLower) ||
        owner.last_name?.toLowerCase().includes(searchLower) ||
        owner.email?.toLowerCase().includes(searchLower) ||
        owner.username?.toLowerCase().includes(searchLower)
      ) || false;
      
      // Check if search term matches secondary owners
      const matchesSecondaryOwners = app.secondary_owners?.some(owner => 
        owner.first_name?.toLowerCase().includes(searchLower) ||
        owner.last_name?.toLowerCase().includes(searchLower) ||
        owner.email?.toLowerCase().includes(searchLower) ||
        owner.username?.toLowerCase().includes(searchLower)
      ) || false;
      
      // Check if search term matches delegates in instances
      const matchesDelegates = getInstances(app).some(instance => 
        instance.delegatedTo?.toLowerCase().includes(searchLower) ||
        instance.assignedTo?.toLowerCase().includes(searchLower)
      );
      
      const matchesSearch = searchTerm === '' || 
        matchesAppDetails || 
        matchesPrimaryOwners || 
        matchesSecondaryOwners || 
        matchesDelegates;

      // Status filter
      const matchesStatus = statusFilter === 'all' || app.status === statusFilter;

      // Risk filter
      const matchesRisk = riskFilter === 'all' || app.risk_level === riskFilter;

      // Environment filter (check if any instance matches)
      const matchesEnvironment = environmentFilter === 'all' || 
        getInstances(app).some(instance => instance.environment === environmentFilter);

      // Platform status filter (check if any instance matches)
      const matchesPlatformStatus = platformStatusFilter === 'all' || 
        getInstances(app).some(instance => 
          instance.igaStatus === platformStatusFilter ||
          instance.ssoStatus === platformStatusFilter ||
          instance.pamStatus === platformStatusFilter
        );

      const taggedDemo = /\b(e2e|placeholder)\b/i.test(app.name || '');
      const matchesDemo = showDemoRows || !taggedDemo;
      return matchesSearch && matchesStatus && matchesRisk && 
             matchesEnvironment && matchesPlatformStatus && matchesDemo;
    });
  };

  // Load applications based on user role
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Clear any local state to ensure fresh data is loaded from database
      localStorage.removeItem('createdInstances');
      localStorage.removeItem('onboardingSubmissions');
      setCreatedInstances({});
      setOnboardingSubmissions({});
      
      // Ensure user object is fully loaded
      if (!user || !user.role) {

        return;
      }

      // Calculate isAdmin once to avoid stale closure issues
      // sso_admin sees only their own apps on /applications (like a regular user)
      // Their enterprise-wide SSO management is on /sso-review
      const currentIsEnterpriseViewer =
        user.role === 'super_admin' ||
        user.role === 'iga_admin' ||
        user.role === 'pam_admin' ||
        user.role === 'grc_admin' ||
        user.role === 'auditor';
      const currentIsAdmin = user?.role === 'super_admin';


      let apps: Application[];
      
      if (currentIsEnterpriseViewer) {
        const response = await applicationsAPI.getApplications();
        apps = response || [];
      } else {
        // For regular users, try to get their applications
        try {
          const response = await applicationsAPI.getApplications();
          // Filter for user's applications (this would ideally be done in backend)
          apps = response || [];
        } catch (err) {

          apps = [];
        }
      }

      setApplications(apps);

      // Load instances for each application in parallel
      const mapInstances = (instances: Awaited<ReturnType<typeof instancesAPI.getInstancesByApplication>>) =>
        instances.map(instance => ({
          ...instance,
          description: instance.description || '',
          primaryOwner: instance.primary_owner_first_name && instance.primary_owner_last_name
            ? `${instance.primary_owner_first_name} ${instance.primary_owner_last_name}`
            : 'Unknown',
          secondaryOwners: (instance as any).secondary_owners ? (instance as any).secondary_owners.map((owner: any) =>
            `${owner.first_name} ${owner.last_name}`
          ) : [],
          igaStatus: instance.iga_status || 'Not Started',
          ssoStatus: instance.sso_status || 'Not Started',
          pamStatus: instance.pam_status || 'Not Started',
          assignedTo: '',
          ssoType: '',
          delegatedTo: '',
          businessJustification: '',
          expectedUserCount: 0,
          dataClassification: '',
          delegationNotes: ''
        }));

      const mapOneInstance = (instance: AppInstance) => mapInstances([instance])[0];

      const instancesData: { [appId: string]: AppInstance[] } = {};
      for (const app of apps) {
        instancesData[app.id] = [];
      }
      try {
        const flat = await instancesAPI.getInstancesByApplicationIdsBulk(apps.map((a) => a.id));
        for (const row of flat) {
          const aid = String((row as AppInstance).application_id || '');
          if (!aid || !instancesData[aid]) continue;
          instancesData[aid].push(mapOneInstance(row as AppInstance));
        }
      } catch (bulkErr) {
        console.warn('Bulk instance load failed, falling back to per-application requests:', bulkErr);
        const instanceEntries = await Promise.all(
          apps.map(async (app) => {
            try {
              const instances = await instancesAPI.getInstancesByApplication(app.id);
              return [app.id, mapInstances(instances)] as const;
            } catch (err) {
              console.error(`Error loading instances for app ${app.name}:`, err);
              return [app.id, []] as const;
            }
          })
        );
        for (const [id, list] of instanceEntries) {
          instancesData[id] = [...list];
        }
      }
      setApplicationInstances(instancesData);

      // Load risk assessments (single bulk request when possible)
      if (user?.role !== 'auditor' && apps.length > 0) {
        const assessmentMap: Record<string, any> = {};
        try {
          const res = await fetch(`${API_ROOT}/risk-assessment/assessments/by-applications`, authFetchInit({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ applicationIds: apps.map((a) => a.id) }),
          }));
          if (res.ok) {
            const data = await res.json();
            const byApp = (data.assessmentsByApplicationId || {}) as Record<string, any>;
            for (const app of apps) {
              assessmentMap[app.id] = byApp[app.id] ?? null;
            }
          } else {
            await Promise.all(
              apps.map(async (app) => {
                try {
                  const r = await fetch(`${API_ROOT}/risk-assessment/assessments/by-application/${app.id}`, authFetchInit());
                  if (r.ok) {
                    const d = await r.json();
                    assessmentMap[app.id] = d.assessment || null;
                  }
                } catch {
                  /* silent */
                }
              })
            );
          }
        } catch {
          await Promise.all(
            apps.map(async (app) => {
              try {
                const r = await fetch(`${API_ROOT}/risk-assessment/assessments/by-application/${app.id}`, authFetchInit());
                if (r.ok) {
                  const d = await r.json();
                  assessmentMap[app.id] = d.assessment || null;
                }
              } catch {
                /* silent */
              }
            })
          );
        }
        setAppAssessments((prev) => ({ ...prev, ...assessmentMap }));
      }

      // Load existing SSO configurations to initialize status
      try {
        let ssoConfigs: any[] = [];
        


        
        if (currentIsAdmin) {
          // Admins can see all configurations
          const response = await ssoAPI.getConfigurationsByEnterprise();
          ssoConfigs = response.data.data || [];
        } else {
          // Regular users see only their own configurations
          try {
            const response = await ssoAPI.getConfigurationsByUser();
            ssoConfigs = response.data.data || [];
          } catch (userErr) {
            console.error('Error loading user SSO configs:', userErr);
            ssoConfigs = [];
          }
        }
        
        if (ssoConfigs && ssoConfigs.length > 0) {
          // Create a map of instance ID to SSO status
          const instanceStatusMap: {[key: string]: string} = {};
          
          // Define status priority (higher number = more advanced)
          const statusPriority: {[key: string]: number} = {
            'Not Started': 0,
            'Pending Review': 1,
            'Need More Info': 2,
            'Rejected': 3,
            'In Progress': 4,
            'Approved': 5,
            'Completed': 6
          };
          
          ssoConfigs.forEach(config => {
            if (config.instance_id) {
              const currentStatus = instanceStatusMap[config.instance_id];
              const currentPriority = currentStatus ? statusPriority[currentStatus] || 0 : -1;
              const newPriority = statusPriority[config.status] || 0;
              
              // Keep the status with higher priority (more advanced)
              if (newPriority > currentPriority) {
                instanceStatusMap[config.instance_id] = config.status;
              }
            }
          });
          

          


          // Update onboardingSubmissions with existing statuses
          setOnboardingSubmissions(prev => {
            const updated = { ...prev };
            apps.forEach(app => {
              // Initialize app if it doesn't exist
              if (!updated[app.id]) {
                updated[app.id] = { instances: {} };
              }
              
                          // Set SSO status for each instance based on actual configuration
            // For External Applications, iterate through all actual instances
            if (app.name === 'External Applications') {
              // Get all instances for this app
              const appInstances = instancesData[app.id] || [];
              
              appInstances.forEach(instance => {
                const instanceStatus = instanceStatusMap[instance.id] || 'Not Started';
                
                updated[app.id].instances[instance.id] = {
                  ...updated[app.id].instances[instance.id],
                  igaStatus: 'Not Started',
                  ssoStatus: instanceStatus,
                  pamStatus: 'Not Started'
                };
              });
            } else {
              // For other apps, use the main instance pattern
              const mainInstanceId = `${app.id}-main`;
              // First try the virtual main key, then fallback to any config for this app
              // (instance_id may be a real UUID when submitted from an instance row)
              const mainStatus = instanceStatusMap[mainInstanceId]
                || ssoConfigs.find(c => c.application_id === app.id)?.status
                || 'Not Started';

              updated[app.id].instances[mainInstanceId] = {
                ...updated[app.id].instances[mainInstanceId],
                igaStatus: 'Not Started',
                ssoStatus: mainStatus,
                pamStatus: 'Not Started'
              };
            }
              
            });
            return updated;
          });
        } else {
          // Initialize all applications with "Not Started" status
          setOnboardingSubmissions(prev => {
            const updated = { ...prev };
            apps.forEach(app => {
              if (!updated[app.id]) {
                updated[app.id] = { instances: {} };
              }
              
              updated[app.id].instances[`${app.id}-main`] = {
                ...updated[app.id].instances[`${app.id}-main`],
                igaStatus: 'Not Started',
                ssoStatus: 'Not Started',
                pamStatus: 'Not Started'
              };
              
            });
            return updated;
          });
        }
      } catch (err) {

        setError(`Failed to load SSO status: ${err instanceof Error ? err.message : 'Unknown error'}`);
        
        // Initialize with "Not Started" as fallback
        setOnboardingSubmissions(prev => {
          const updated = { ...prev };
          apps.forEach(app => {
            if (!updated[app.id]) {
              updated[app.id] = { instances: {} };
            }
            
            updated[app.id].instances[`${app.id}-main`] = {
              ...updated[app.id].instances[`${app.id}-main`],
              igaStatus: 'Not Started',
              ssoStatus: 'Not Started',
              pamStatus: 'Not Started'
            };
            
          });
          return updated;
        });
      }

      // Full user list for admin owner pickers; active users only for app owners (delegation)
      const canLoadFullUsers =
        currentIsAdmin ||
        user?.role === 'app_admin' ||
        user?.role === 'sso_admin' ||
        user?.role === 'iga_admin' ||
        user?.role === 'pam_admin';
      try {
        const usersResponse = canLoadFullUsers
          ? await usersAPI.getUsers()
          : user
            ? await usersAPI.getUsersForPicker()
            : [];
        setUsers(usersResponse || []);
      } catch (err) {
        console.error('Failed to load users:', err);
      }
    } catch (err) {
      setError('Failed to load data');

    } finally {
      setLoading(false);
    }
  }, [user]);

  // Bulk unread badges — one notes-summary call instead of N GET /notes
  const updateUnreadCommentCounts = useCallback(async () => {
    try {
      // Build last_viewed keyed by SSO config id (localStorage + in-memory instance map)
      const lastViewedByConfig: Record<string, string> = {};
      for (const config of ssoConfigurations) {
        if (!config?.id) continue;
        const fromLs = localStorage.getItem(`lastViewedComments_${config.id}`);
        const fromState = config.instance_id ? lastViewedComments[config.instance_id] : undefined;
        const value = fromLs || fromState;
        if (value) lastViewedByConfig[config.id] = value;
      }

      const response = await ssoAPI.getNotesSummary(lastViewedByConfig);
      const summary = response.data.data || [];
      const byConfigId = new Map(summary.map((row) => [row.configuration_id, row.unread_count]));
      const newUnreadComments: { [instanceId: string]: number } = {};
      for (const config of ssoConfigurations) {
        if (!config?.instance_id || !config?.id) continue;
        const unread = byConfigId.get(config.id) || 0;
        if (unread > 0) newUnreadComments[config.instance_id] = unread;
      }
      setUnreadComments(newUnreadComments);
    } catch (err) {
      console.error('Error loading unread note counts:', err);
    }
  }, [ssoConfigurations, lastViewedComments]);

  // Certificate expiration utility functions
  const formatCertificateExpiration = (expirationDate: Date): string => {
    const now = new Date();
    const timeDiff = expirationDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysRemaining < 0) {
      return `Expired ${Math.abs(daysRemaining)} days ago`;
    } else if (daysRemaining === 0) {
      return 'Expires today';
    } else if (daysRemaining === 1) {
      return 'Expires tomorrow';
    } else if (daysRemaining <= 30) {
      return `Expires in ${daysRemaining} days`;
    } else {
      return expirationDate.toLocaleDateString();
    }
  };

  const getCertificateExpirationColor = (expirationDate: Date): 'default' | 'warning' | 'error' => {
    const now = new Date();
    const timeDiff = expirationDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysRemaining < 0) {
      return 'error'; // Expired
    } else if (daysRemaining <= 30) {
      return 'warning'; // Expiring soon
    } else {
      return 'default'; // Valid
    }
  };

  const loadSsoStatistics = async () => {
    try {
      const response = await ssoAPI.getStatistics();
      setSsoStatistics(response.data.data);
    } catch (err) {
      console.error('Error loading SSO statistics:', err);
    }
  };

  const loadSsoConfigurations = async () => {
    try {
      let data;
      if (isSuperAdmin || isSSOAdmin) {
        // Admins can see all configurations
        const response = await ssoAPI.getConfigurationsByEnterprise();
        data = response.data.data || [];
      } else {
        // Regular users see only their own configurations
        const response = await ssoAPI.getConfigurationsByUser();
        data = response.data.data || [];
      }
      setSsoConfigurations(data);
    } catch (err) {
      console.error('Error loading SSO configurations:', err);
      // Set empty data on error to prevent UI issues
      setSsoConfigurations([]);
    }
  };

  const getApplicationSSOStatus = (appId: string): string => {
    // Check if there are any SSO configurations for this application
    const appSubmissions = onboardingSubmissions[appId];
    if (!appSubmissions || !appSubmissions.instances) {
      return 'Not Started';
    }

    // Get all instance statuses for this application
    const instanceStatuses = Object.values(appSubmissions.instances).map(instance => instance.ssoStatus);
    
    // Return the most advanced status
    if (instanceStatuses.includes('Completed')) return 'Completed';
    if (instanceStatuses.includes('Approved')) return 'Approved';
    if (instanceStatuses.includes('In Progress')) return 'In Progress';
    if (instanceStatuses.includes('Rejected')) return 'Rejected';
    if (instanceStatuses.includes('Need More Info')) return 'Need More Info';
    if (instanceStatuses.includes('Pending Review')) return 'Pending Review';
    
    return 'Not Started';
  };

  const exportToCSV = () => {
    const csvData = applications.map(app => {
      const primaryOwners = app.primary_owners?.map(owner => owner.username).join('; ') || 'N/A';
      const secondaryOwners = app.secondary_owners?.map(owner => owner.username).join('; ') || 'N/A';
      
      // Get SSO status for this application
      const appSSOStatus = getApplicationSSOStatus(app.id);
      
      return {
        'Application Name': app.name,
        'Description': app.description || 'N/A',
        'Business Function': app.business_function || 'N/A',
        'Risk Level': app.risk_level || 'N/A',
        'Status': app.status,
        'SSO Status': appSSOStatus,
        'Primary Owners': primaryOwners,
        'Secondary Owners': secondaryOwners,
        'Created At': new Date(app.created_at).toLocaleDateString(),
        'Updated At': new Date(app.updated_at).toLocaleDateString()
      };
    });

    // Convert to CSV
    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => 
        headers.map(header => {
          const value = row[header as keyof typeof row];
          // Escape commas and quotes in CSV
          return typeof value === 'string' && (value.includes(',') || value.includes('"')) 
            ? `"${value.replace(/"/g, '""')}"` 
            : value;
        }).join(',')
      )
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `applications_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCSV = (csvText: string): any[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(value => value.trim().replace(/"/g, ''));
      if (values.length === headers.length) {
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
    }

    return data;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setImportError('Please select a CSV file');
      return;
    }

    setImportFile(file);
    setImportError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target?.result as string;
      const parsedData = parseCSV(csvText);
      setImportPreview(parsedData);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importPreview.length) return;

    setImportLoading(true);
    setImportError(null);

    try {
      const importData = importPreview.map(row => ({
        name: row['Application Name'] || row['Name'] || '',
        description: row['Description'] || '',
        application_id: row['Application ID'] || row['App ID'] || '',
        business_function: row['Business Function'] || '',
        risk_level: row['Risk Level'] || 'Not Defined',
        status: row['Status'] || 'Active',
        primary_owner_ids: [], // Will be handled separately
        secondary_owner_ids: []
      }));

      // Import applications one by one
      const results = [];
      for (const appData of importData) {
        if (appData.name) {
          try {
            const result = await applicationsAPI.createApplication(appData);
            results.push({ success: true, data: result, name: appData.name });
          } catch (err) {
            results.push({ success: false, error: err, name: appData.name });
          }
        }
      }

      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        setSuccess(`Successfully imported ${successCount} applications${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
        await loadData(); // Refresh the applications list
      }

      if (errorCount > 0) {
        setImportError(`${errorCount} applications failed to import. Please check the data format.`);
      }

      setImportDialogOpen(false);
      setImportFile(null);
      setImportPreview([]);

    } catch (err) {
      setImportError('Failed to import applications. Please check the file format.');
    } finally {
      setImportLoading(false);
    }
  };

    // Load data when component mounts or when user/admin status changes
  useEffect(() => {
    if (user && user.role) {
    loadData();
      // Load SSO statistics for all users to show status tiles
      loadSsoStatistics();
      // Load SSO configurations for assignment and comments data
      loadSsoConfigurations();
    }
  }, [user, loadData]);

  // Poll for SSO configuration updates every 60 seconds to get status changes
  useEffect(() => {
    if (!user || !user.role) return;

    const pollInterval = setInterval(() => {
      loadSsoConfigurations();
    }, 60000);

    return () => clearInterval(pollInterval);
  }, [user]);

  // When SSO configurations are loaded, refresh unread comment badges (one bulk API call)
  useEffect(() => {
    if (ssoConfigurations.length > 0) {
      void updateUnreadCommentCounts();
    }
  }, [ssoConfigurations, updateUnreadCommentCounts]);

  // Handle URL parameter changes for tab switching
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
    setActiveTab(tab);
    } else {
      // If no tab parameter, use the route-based tab setting
      if (currentOnboardingType === 'sso') {
        setActiveTab('sso');
      } else if (currentOnboardingType === 'iga') {
        setActiveTab('iga');
      } else if (currentOnboardingType === 'pam') {
        setActiveTab('pam');
      } else {
        setActiveTab('all');
      }
    }
  }, [searchParams, currentOnboardingType]);

  // Listen for comments viewed events
  useEffect(() => {
    const handleCommentsViewed = (event: CustomEvent) => {
      console.log('🔔 Comments viewed event received:', event.detail);
      const { configId, lastViewed } = event.detail;
      
      // Find the instance ID for this config
      const config = ssoConfigurations.find(c => c.id === configId);
      if (config && config.instance_id) {
        console.log(`✅ Found config for instance: ${config.instance_id}`);
        // Update the lastViewedComments state
        setLastViewedComments(prev => ({
          ...prev,
          [config.instance_id]: lastViewed
        }));
        
        // Update unread comment counts
        console.log('🔄 Updating unread comment counts...');
        void updateUnreadCommentCounts();
      } else {
        console.log(`❌ No config found for configId: ${configId}`);
      }
    };

    window.addEventListener('commentsViewed', handleCommentsViewed as EventListener);
    
    return () => {
      window.removeEventListener('commentsViewed', handleCommentsViewed as EventListener);
    };
  }, [ssoConfigurations]);

  // Reset main pagination when filters change
  useEffect(() => {
    setMainCurrentPage(1);
  }, [searchTerm, statusFilter, riskFilter, environmentFilter, platformStatusFilter, mainItemsPerPage, listingSort, showDemoRows]);

  const handleOpenDialog = (app?: Application) => {
    if (app) {
      setEditingApplication(app);
      setFormData({
        name: app.name,
        description: app.description,
        application_id: app.application_id,
        business_function: app.business_function,
        risk_level: app.risk_level,
        status: app.status,
        primary_owner_ids: app.primary_owners?.map(owner => owner.user_id) || [],
        secondary_owner_ids: app.secondary_owners?.map(owner => owner.user_id) || []
      });
    } else {
      setEditingApplication(null);
      setFormData({
        name: '',
        description: '',
        application_id: `APP-${Date.now().toString(36).toUpperCase()}`,
        business_function: '',
        risk_level: 'Not Defined',
        status: 'Active',
        primary_owner_ids: [],
        secondary_owner_ids: []
      });
    }
    setSetupStep(app ? 2 : 0);
    setSetupProtocol('SAML');
    setSetupEnvironment('Production');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingApplication(null);
    setSetupStep(0);
    setFormData({
      name: '',
      description: '',
      application_id: '',
      business_function: '',
      risk_level: 'Not Defined',
      status: 'Active'
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate that super admin has set required owner fields
      if (isSuperAdmin && (!formData.primary_owner_ids || formData.primary_owner_ids.length === 0)) {
        setError('At least one Primary Owner is required for Super Administrators');
        return;
      }

      const payload = editingApplication ? formData : {
        ...formData,
        description: `${formData.description}\nProtocol: ${setupProtocol}. Environment: ${setupEnvironment}.`.trim(),
      };
      if (editingApplication) {
        await applicationsAPI.updateApplication(editingApplication.id, formData);
        setSuccess('Application updated successfully');
      } else {
        await applicationsAPI.createApplication(payload);
        setSuccess('Application created successfully');
      }

      handleCloseDialog();
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, editingApplication ? 'Failed to update application' : 'Failed to create application'));

    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (appId: string) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        setLoading(true);
        await applicationsAPI.deleteApplication(appId);
        setSuccess('Application deleted successfully');
        await loadData();
      } catch (err) {
        setError('Failed to delete application');

      } finally {
        setLoading(false);
      }
    }
  };

  const isApplicationOwner = (app: Application | null | undefined) => {
    if (!user || !app) return false;
    return app.primary_owners?.some(owner => owner.user_id === user.id) ||
           app.secondary_owners?.some(owner => owner.user_id === user.id);
  };

  const delegateRowsForUser = (app: Application) =>
    (app.delegates || []).filter((delegate) => delegate.user_id === user?.id);

  const isApplicationDelegate = (app: Application | null | undefined) => {
    if (!user || !app) return false;
    const today = calendarDay(new Date());
    const rows = delegateRowsForUser(app);
    if (rows.length > 0) return rows.some((delegate) => delegationAllows(delegate, today, 'view') || delegationIsCurrent(delegate, today));
    return app.is_delegate === true;
  };

  const delegateHasScope = (app: Application, scope: 'view' | 'edit' | 'submit_sso') => {
    if (!user) return false;
    const today = calendarDay(new Date());
    return delegateRowsForUser(app).some((delegate) => delegationAllows(delegate, today, scope));
  };

  const canManageApplication = (app: Application | null | undefined) => {
    if (!user || !app) return false;
    return isSuperAdmin || isApplicationOwner(app) || delegateHasScope(app, 'edit');
  };

  const canManageInstances = (app: Application | null | undefined) => {
    if (!user || !app) {
      console.log('canManageInstances: No user or app', { user: !!user, app: !!app });
      return false;
    }
    
    const isOwner = isApplicationOwner(app);
    const isDelegate = isApplicationDelegate(app);
    const canManage = isSuperAdmin || isOwner || delegateHasScope(app, 'edit');
    
    console.log('canManageInstances debug:', {
      userId: user.id,
      appId: app.id,
      appName: app.name,
      isSuperAdmin,
      isOwner,
      isDelegate,
      canManage,
      primaryOwners: app.primary_owners,
      secondaryOwners: app.secondary_owners
    });
    
    // Delegates cannot manage instances, only onboard
    return canManage;
  };

  const canOnboardInstances = (app: Application | null | undefined) => {
    if (!user || !app) return false;
    return isSuperAdmin || isApplicationOwner(app) || delegateHasScope(app, 'submit_sso');
  };

  /** App owners (not delegates), SSO admins, and super admins can renew secrets / view OIDC credentials. */
  const canManageSsoSecrets = (app: Application | null | undefined) => {
    if (!user || !app) return false;
    return isSuperAdmin || isSSOAdmin || canManageInstances(app);
  };

  const canRenewOidcSecret = (app: Application | null | undefined, config: any) =>
    !!config &&
    config.sso_type === 'OIDC' &&
    !!config.client_id &&
    canManageSsoSecrets(app);

  const canViewOidcCredentials = (app: Application | null | undefined, config: any) =>
    !!config &&
    config.sso_type === 'OIDC' &&
    !!config.client_id &&
    canManageSsoSecrets(app);

  const canPlatformOnboard = (app: Application, platformName: string) => {
    if (canOnboardInstances(app)) return true;
    if (isSuperAdmin) return true;
    const platformKey = platformName.toLowerCase();
    if (isIGAAdmin && platformKey === 'iga') return true;
    if (isPAMAdmin && platformKey === 'pam') return true;
    return false;
  };

  // Returns true if GRC has approved the assessment OR an admin manually set a risk level (pre-existing risk posture).
  // A re-submitted/in-progress assessment takes precedence — don't treat as approved if there's an active review.
  const isRiskApproved = (appId: string, appRiskLevel?: string) => {
    const status = appAssessments[appId]?.status;
    if (status && status !== 'Approved') return false; // in-progress reassessment
    if (status === 'Approved') return true;
    return ['Low', 'Medium', 'High', 'Critical'].includes(appRiskLevel || '');
  };

  const getRiskChipStyle = (level: string | undefined) => {
    const surface = getRiskSurface(level || '', resolvedMode);
    return { bg: surface.bg, color: surface.fg, border: surface.border };
  };

  const getEnvChipStyle = (env: string) => {
    const surface = getEnvironmentSurface(env, resolvedMode);
    return { bg: surface.bg, color: surface.fg, border: surface.border };
  };

  const loadAppAssessment = async (appId: string) => {
    try {
      const res = await fetch(`${API_ROOT}/risk-assessment/assessments/by-application/${appId}`, authFetchInit());
      if (res.ok) {
        const data = await res.json();
        setAppAssessments(prev => ({ ...prev, [appId]: data.assessment || null }));
      }
    } catch (e) {
      // silent
    }
  };

  const handleOpenRiskAssessment = async (app: Application) => {
    // Reset all dialog state before loading
    setRaApp(app);
    setRaAssessmentId(null);
    setRaQuestions([]);
    setRaAnswers({});
    setRaReviewNotes('');
    setRaError(null);
    setRaActiveTab(0);
    setRaAssessmentStatus(null);
    setRaControlPreview(null);
    setRaLoading(true);
    setRaDialogOpen(true);
    try {
      // Load template + questions
      const tplRes = await fetch(`${API_ROOT}/risk-assessment/template`, authFetchInit());
      if (!tplRes.ok) {
        throw new Error(`Failed to load questionnaire template (${tplRes.status})`);
      }
      const tplData = await tplRes.json();
      if (!tplData.template) {
        throw new Error('No active risk questionnaire template found. Ask your GRC Admin to set one up.');
      }
      const questions = (tplData.template.questions || []).filter((q: any) => q.is_visible !== false && !/\b(e2e|placeholder|lorem ipsum)\b/i.test(String(q.question_text || '')));
      setRaQuestions(questions);

      // Load or create assessment for this app — always re-fetch to get latest status
      const existingRes = await fetch(`${API_ROOT}/risk-assessment/assessments/by-application/${app.id}`, authFetchInit());
      const existingData = existingRes.ok ? await existingRes.json() : { assessment: null };
      const existing = existingData.assessment;

      if (existing) {
        setRaAssessmentId(existing.id);
        setRaAnswers(existing.answers || {});
        setRaReviewNotes(existing.review_notes || '');
        setRaAssessmentStatus(existing.status || null);
        setRaControlPreview(ownerControlPreview(existing));
        // Keep appAssessments in sync
        setAppAssessments(prev => ({ ...prev, [app.id]: existing }));
      } else {
        // Create a new draft assessment linked to this app
        const createRes = await fetch(`${API_ROOT}/risk-assessment/assessments`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            template_id: tplData.template.id,
            application_id: app.id,
            enterprise_id: '00000000-0000-0000-0000-000000000001',
            submitted_by: user?.id,
            status: 'Draft'
          })
        });
        if (!createRes.ok) {
          const errData = await createRes.json().catch(() => ({}));
          throw new Error(errData.message || `Failed to create assessment (${createRes.status})`);
        }
        const createData = await createRes.json();
        if (!createData.assessment_id) {
          throw new Error('Assessment created but no ID returned from server.');
        }
        setRaAssessmentId(createData.assessment_id);
        setRaAnswers({});
        setRaReviewNotes('');
        // Immediately reflect the new draft in the app card (prevents "Start Assessment" on close)
        setAppAssessments(prev => ({ ...prev, [app.id]: { id: createData.assessment_id, status: 'Draft', answers: {} } }));
      }
    } catch (e: any) {
      console.error('Error opening risk assessment:', e);
      setRaError(e.message || 'Failed to load risk assessment. Please try again.');
    } finally {
      setRaLoading(false);
    }
  };

  const handleSaveRiskAnswers = async () => {
    if (!raAssessmentId) {
      setRaError('Assessment not initialized. Please close and reopen this dialog.');
      return;
    }
    setRaSubmitting(true);
    setRaError(null);
    try {
      const res = await fetch(`${API_ROOT}/risk-assessment/assessments/${raAssessmentId}/answers`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ answers: raAnswers, user_id: user?.id })
      });
      const saved = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(saved.message || `Save failed (${res.status})`);
      }
      if (saved.owner_sentence || saved.control_name) {
        setRaControlPreview({
          score: typeof saved.score === 'number' ? saved.score : null,
          name: saved.control_name || '',
          sentence: saved.owner_sentence || '',
        });
      }
      showSuccess?.('Progress saved');
    } catch (e: any) {
      console.error('Failed to save risk assessment answers:', e);
      setRaError(e.message || 'Failed to save progress. Please try again.');
    } finally {
      setRaSubmitting(false);
    }
  };

  const handleSubmitRiskAssessment = async () => {
    if (!raAssessmentId) {
      setRaError('Assessment not initialized. Please close and reopen this dialog.');
      return;
    }
    if (!raApp) return;
    setRaSubmitting(true);
    setRaError(null);
    try {
      // Save answers first
      const saveRes = await fetch(`${API_ROOT}/risk-assessment/assessments/${raAssessmentId}/answers`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ answers: raAnswers, user_id: user?.id })
      });
      if (!saveRes.ok) {
        const errData = await saveRes.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to save answers (${saveRes.status})`);
      }
      // Submit for review
      const submitRes = await fetch(`${API_ROOT}/risk-assessment/assessments/${raAssessmentId}/submit`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ user_id: user?.id })
      });
      if (!submitRes.ok) {
        const errData = await submitRes.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to submit for review (${submitRes.status})`);
      }
      setRaDialogOpen(false);
      // Reload app assessment and application list to reflect 'In Review'
      await loadAppAssessment(raApp.id);
      loadData();
      showSuccess?.('Risk Assessment submitted for GRC review');
    } catch (e: any) {
      console.error('Failed to submit risk assessment:', e);
      setRaError(e.message || 'Failed to submit assessment. Please try again.');
    } finally {
      setRaSubmitting(false);
    }
  };

  /** Deep link: /applications?action=add opens Add Application (manual form on Applications page). */
  const addActionConsumedRef = useRef(false);
  useEffect(() => {
    if (addActionConsumedRef.current) return;
    const action = searchParams.get('action');
    if (action !== 'add') return;
    if (!isSuperAdmin) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('action');
        return next;
      }, { replace: true });
      return;
    }
    addActionConsumedRef.current = true;
    const prefillName = searchParams.get('name') || '';
    handleOpenDialog();
    if (prefillName) {
      setFormData(prev => ({ ...prev, name: prefillName }));
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('action');
      next.delete('name');
      return next;
    }, { replace: true });
  }, [searchParams, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Deep link: /applications?openRisk=<applicationId> opens the risk questionnaire dialog. */
  const riskOpenLinkRef = useRef<string | null>(null);
  useEffect(() => {
    const id = searchParams.get('openRisk');
    if (!id) {
      riskOpenLinkRef.current = null;
      return;
    }
    if (loading || applications.length === 0) return;
    if (riskOpenLinkRef.current === id) return;
    const app = applications.find(a => a.id === id);
    if (!app) {
      riskOpenLinkRef.current = id;
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('openRisk');
        return next;
      }, { replace: true });
      return;
    }
    riskOpenLinkRef.current = id;
    void handleOpenRiskAssessment(app).finally(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('openRisk');
        return next;
      }, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleOpenRiskAssessment is stable for this deep-link; adding it recreates a large callback graph
  }, [applications, loading, searchParams, setSearchParams]);

  const handleOnboard = async (appId: string, platform: string, instanceId?: string) => {
    try {
      if (platform === 'SSO') {
        const app = applications.find(a => a.id === appId);
        if (app) {
          setSelectedAppForSSO(app);
          setSelectedInstanceForSSO(instanceId || `${appId}-prod`); // Default to Production
          setSsoDialogOpen(true);
        }
        return;
      }

      setLoading(true);

      
      // For now, only SSO onboarding is implemented
      // IGA and PAM will show "Not Started" until their onboarding is implemented
      setSuccess(`Onboarding to ${platform} initiated! (Backend integration pending)`);
      await loadData();
    } catch (err) {
      setError(`Failed to onboard application to ${platform}`);

    } finally {
      setLoading(false);
    }
  };

  const handleSSODialogClose = () => {
    setSsoDialogOpen(false);
    
    // Update the onboarding status to reflect SSO submission for the specific instance
    if (selectedAppForSSO && selectedInstanceForSSO) {

      
      setOnboardingSubmissions(prev => {
        const newState = { ...prev };
        
        // Initialize app if it doesn't exist
        if (!newState[selectedAppForSSO.id]) {
          newState[selectedAppForSSO.id] = { instances: {} };
        }
        
        // Set SSO status only for the specific instance
        newState[selectedAppForSSO.id].instances[selectedInstanceForSSO] = {
          ...newState[selectedAppForSSO.id].instances[selectedInstanceForSSO],
          igaStatus: 'Not Started',
          ssoStatus: 'Pending Review',
          pamStatus: 'Not Started'
        };
        

        return newState;
      });
      
      setSuccess(`SSO onboarding submitted successfully for ${selectedInstanceForSSO}! Status updated to "Pending Review"`);
      
      // Refresh the data to ensure consistency
      setTimeout(() => {
        loadData();
        loadSsoConfigurations();
      }, 1000);
    }
    
    setSelectedAppForSSO(null);
    setSelectedInstanceForSSO('');
  };

  const getOnboardingStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'approved':
        return 'info';
      case 'in progress':
        return 'warning';
      case 'pending review':
        return 'secondary';
      case 'rejected':
        return 'error';
      case 'need more info':
        return 'warning';
      case 'not started':
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'approved':
        return <InfoIcon color="info" />;
      case 'in progress':
        return <PendingIcon color="warning" />;
      case 'pending review':
        return <WarningIcon color="warning" />;
      case 'rejected':
        return <ErrorIcon color="error" />;
      case 'need more info':
        return <HelpOutlineIcon color="warning" />;
      case 'not started':
      default:
        return <InfoIcon color="disabled" />;
    }
  };

  const handleOpenInstanceDialog = (app: Application) => {
    setSelectedAppForInstance(app);
    setInstanceFormData({
      name: '',
      environment: 'Production',
      description: ''
    });
    setInstanceDialogOpen(true);
  };

  const handleCloseInstanceDialog = () => {
    setInstanceDialogOpen(false);
    setSelectedAppForInstance(null);
    setEditingInstance(null);
    setInstanceFormData({
      name: '',
      environment: 'Production',
      description: ''
    });
  };

  const handleInstanceSubmit = async () => {
    if (!selectedAppForInstance) return;

    if (!isSuperAdmin && !canManageInstances(selectedAppForInstance)) {
      setError('You can only create instances for applications you own.');
      return;
    }
    
    try {
      setLoading(true);

      if (editingInstance) {
        await instancesAPI.updateInstance(editingInstance.id, {
          name: instanceFormData.name,
          environment: instanceFormData.environment,
          description: instanceFormData.description,
        });
        setSuccess('Instance updated successfully!');
        handleCloseInstanceDialog();
        await loadData();
        return;
      }
      
      // Create instance data - owners are inherited from application
      const instanceData = {
        application_id: selectedAppForInstance.id,
        name: instanceFormData.name,
        environment: instanceFormData.environment,
        description: instanceFormData.description,
        primary_owner_id: selectedAppForInstance.primary_owners?.[0]?.user_id,
        secondary_owner_ids: selectedAppForInstance.secondary_owners?.map(owner => owner.user_id) || []
      };
      
      await instancesAPI.createInstance(instanceData);
      
      setSuccess('Instance created successfully!');
      handleCloseInstanceDialog();
      
      // Reload data to show the new instance
      await loadData();
    } catch (err) {
      console.error('Instance submit error:', err);
      setError(editingInstance ? 'Failed to update instance. Please try again.' : 'Failed to create instance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShowOwners = (application: Application) => {
    setSelectedAppForOwners(application);
    setOwnersDialogOpen(true);
  };

  const handleCloseOwners = () => {
    setOwnersDialogOpen(false);
    setSelectedAppForOwners(null);
  };

  // Delegation dialog handlers
  const handleOpenDelegationDialog = async (app: Application) => {
    setSelectedAppForDelegation(app);
    setDelegationFormData({
      delegateTo: '',
      businessJustification: ''
    });
    
    try {
      const usersResponse = await usersAPI.getUsersForPicker();
      setUsers(usersResponse || []);
    } catch (err) {
      console.error('Failed to load users for delegation:', err);
      setError('Failed to load users for delegation');
    }
    
    setDelegationDialogOpen(true);
  };

  const handleEditInstance = (app: Application, instance: AppInstance) => {
    setSelectedAppForInstance(app);
    setEditingInstance(instance);
    setInstanceFormData({
      name: instance.name,
      environment: instance.environment,
      description: instance.description || ''
    });
    setInstanceDialogOpen(true);
  };

  const handleNotesClick = (instanceId: string) => {
    // For now, just show a notification that this feature is coming soon
    showNotificationMessage('Notes feature is coming soon!', 'info');
  };

  // Application delegates management functions
  const handleOpenDelegatesDialog = async (app: Application) => {
    setSelectedAppForDelegates(app);
    setNewDelegateFormData(emptyDelegateForm);
    
    // Load delegates from API
    try {
      const delegates = await delegationAPI.getApplicationDelegates(app.id);
      setApplicationDelegates(delegates);
      
      // Update the application with delegates from API
      const updatedApp = { ...app, delegates };
      setApplications(prev => prev.map(application => 
        application.id === app.id ? updatedApp : application
      ));
    } catch (err) {
      console.error('Error loading delegates:', err);
      setApplicationDelegates(app.delegates || []);
    }
    
    // Debug user authentication and permissions
    console.log('🔍 Delegation Debug Info:');
    console.log('  - User object:', user);
    console.log('  - User role:', user?.role);
    console.log('  - User ID:', user?.id);
    console.log('  - Is admin:', isAdmin);
    console.log('  - Is app owner:', isAppOwner);
    console.log('  - Can manage application:', canManageApplication(app));
    
    try {
      const usersResponse = await usersAPI.getUsersForPicker();
      setUsers(usersResponse || []);
    } catch (err: any) {
      console.error('Failed to load users for delegates management:', err);
      setError('Failed to load users for delegates management');
    }
    
    setDelegatesDialogOpen(true);
  };

  const handleCloseDelegatesDialog = () => {
    setDelegatesDialogOpen(false);
    setSelectedAppForDelegates(null);
    setApplicationDelegates([]);
    setNewDelegateFormData(emptyDelegateForm);
  };

  const handleAddDelegate = async () => {
    const formError = delegationValidationError({
      userId: newDelegateFormData.userIds[0],
      reason: newDelegateFormData.businessJustification,
      startsAt: newDelegateFormData.startsAt,
      endsAt: newDelegateFormData.endsAt,
      scopes: newDelegateFormData.scopes,
    });
    if (!selectedAppForDelegates || formError) {
      setError(formError || 'Choose a person, a reason, a start, an end, and a scope.');
      return;
    }

    if (!user?.id) {
      setError('Missing user information for delegation');
      return;
    }

    try {
      setLoading(true);
      
      const newDelegates: ApplicationDelegate[] = [];
      
      // Create delegations for each selected user
      for (const userId of newDelegateFormData.userIds) {
        const newDelegate = await delegationAPI.createDelegation({
          application_id: selectedAppForDelegates.id,
          user_id: userId,
          delegated_by: user.id,
          reason: newDelegateFormData.businessJustification,
          business_justification: newDelegateFormData.businessJustification,
          technical_notes: '',
          status: 'active',
          starts_at: newDelegateFormData.startsAt,
          ends_at: newDelegateFormData.endsAt,
          scope: newDelegateFormData.scopes.join(','),
        });
        newDelegates.push(newDelegate);
      }

      // Add to local state
      setApplicationDelegates(prev => [...prev, ...newDelegates]);
      
      // Update the application in the applications array
      const updatedApp = { ...selectedAppForDelegates, delegates: [...(selectedAppForDelegates.delegates || []), ...newDelegates] };
      setApplications(prev => prev.map(app => 
        app.id === selectedAppForDelegates.id 
          ? updatedApp
          : app
      ));

      // Update the selected app for owners dialog if it's the same app
      if (selectedAppForOwners?.id === selectedAppForDelegates.id) {
        setSelectedAppForOwners(updatedApp);
      }

      const userCount = newDelegateFormData.userIds.length;
      const selectedUsers = users.filter(u => newDelegateFormData.userIds.includes(u.id));
      const userNames = selectedUsers.map(u => `${u.first_name} ${u.last_name}`).join(', ');
      
      setSuccess(`${userCount} delegate${userCount > 1 ? 's' : ''} added successfully: ${userNames}`);
      setNewDelegateFormData(emptyDelegateForm);
    } catch (err) {
      console.error('Add delegate error:', err);
      setError('Failed to add delegates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDelegate = async (delegateId: string) => {
    try {
      setLoading(true);
      
      // Delete delegation via API
      await delegationAPI.deleteDelegation(delegateId);
      
      // Remove from local state
      setApplicationDelegates(prev => prev.filter(d => d.id !== delegateId));
      
      // Update the application in the applications array
      const updatedApp = { ...selectedAppForDelegates!, delegates: (selectedAppForDelegates!.delegates || []).filter(d => d.id !== delegateId) };
      setApplications(prev => prev.map(app => 
        app.id === selectedAppForDelegates?.id 
          ? updatedApp
          : app
      ));

      // Update the selected app for owners dialog if it's the same app
      if (selectedAppForOwners?.id === selectedAppForDelegates?.id) {
        setSelectedAppForOwners(updatedApp);
      }

      setSuccess('Delegate removed successfully!');
    } catch (err) {
      console.error('Remove delegate error:', err);
      setError('Failed to remove delegate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Application management functions
  const handleEditApplication = (app: Application) => {
    setEditingApplication(app);
    setFormData({
      name: app.name,
      description: app.description,
      application_id: app.application_id,
      business_function: app.business_function,
      risk_level: app.risk_level,
      status: app.status,
      primary_owner_ids: app.primary_owners?.map(owner => owner.user_id) || [],
      secondary_owner_ids: app.secondary_owners?.map(owner => owner.user_id) || []
    });
    setDialogOpen(true);
  };

  const handleDeleteApplication = async (app: Application) => {
    if (window.confirm(`Are you sure you want to delete the application "${app.name}"? This action cannot be undone.`)) {
      try {
        setLoading(true);
        
        // Call backend API to delete from database
        await applicationsAPI.deleteApplication(app.id);
        
        setSuccess(`Application "${app.name}" deleted successfully!`);
        
        // Reload data from database to ensure consistency
      await loadData();
    } catch (err) {
        console.error('Delete application error:', err);
        setError('Failed to delete application. Please try again.');
    } finally {
      setLoading(false);
      }
    }
  };

  const handleCloseDelegationDialog = () => {
    setDelegationDialogOpen(false);
    setSelectedAppForDelegation(null);
    setDelegationFormData({
      delegateTo: '',
      businessJustification: ''
    });
  };

  const handleDelegationSubmit = async () => {
    try {
      setLoading(true);
      
      if (!delegationFormData.delegateTo || !delegationFormData.businessJustification) {
        setError('Please select a delegate and provide business justification for delegation');
        return;
      }
      
      if (!selectedAppForDelegation?.id || !user?.id) {
        setError('Missing required information for delegation');
        return;
      }

      // Create delegation via API
      const newDelegation = await delegationAPI.createDelegation({
        application_id: selectedAppForDelegation.id,
        user_id: delegationFormData.delegateTo,
        delegated_by: user.id,
        reason: delegationFormData.businessJustification,
        business_justification: delegationFormData.businessJustification,
        technical_notes: '',
        status: 'active'
      });

      // Update local state with the new delegation from API
      setApplications(prev => prev.map(app => 
        app.id === selectedAppForDelegation.id 
          ? { ...app, delegates: [...(app.delegates || []), newDelegation] }
          : app
      ));

      const delegatedUser = users.find(u => u.id === delegationFormData.delegateTo);
      setSuccess(`Instance onboarding delegated to ${delegatedUser?.first_name} ${delegatedUser?.last_name} successfully!`);
      
      handleCloseDelegationDialog();
    } catch (err) {
      console.error('Delegation error:', err);
      setError('Failed to delegate instance onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reporting functions
  const generateInactiveUsersReport = () => {
    const report: any[] = [];
    
    applications.forEach(app => {
      const instances = getInstances(app);
      
      // Check primary owners
      app.primary_owners?.forEach(owner => {
        const user = users.find(u => u.id === owner.user_id);
        if (user && !user.is_active) {
          report.push({
            applicationId: app.id,
            applicationName: app.name,
            userType: 'Primary Owner',
            userId: owner.user_id,
            userName: `${user.first_name} ${user.last_name}`,
            userEmail: user.email,
            isActive: user.is_active,
            lastLogin: 'Never',
            status: 'Inactive'
          });
        }
      });
      
      // Check secondary owners
      app.secondary_owners?.forEach(owner => {
        const user = users.find(u => u.id === owner.user_id);
        if (user && !user.is_active) {
          report.push({
            applicationId: app.id,
            applicationName: app.name,
            userType: 'Secondary Owner',
            userId: owner.user_id,
            userName: `${user.first_name} ${user.last_name}`,
            userEmail: user.email,
            isActive: user.is_active,
            lastLogin: 'Never',
            status: 'Inactive'
          });
        }
      });
      
      // Check delegates in instances
      instances.forEach(instance => {
        if (instance.assignedTo) {
          const delegateUser = users.find(u => u.email === instance.assignedTo || `${u.first_name} ${u.last_name}` === instance.assignedTo);
          if (delegateUser && !delegateUser.is_active) {
            report.push({
              applicationId: app.id,
              applicationName: app.name,
              instanceId: instance.id,
              instanceName: instance.name,
              userType: 'Delegate',
              userId: delegateUser.id,
              userName: `${delegateUser.first_name} ${delegateUser.last_name}`,
              userEmail: delegateUser.email,
              isActive: delegateUser.is_active,
              lastLogin: 'Never',
              status: 'Inactive'
            });
          }
        }
      });
    });
    
    return report;
  };

  const generateAllInactiveReport = () => {
    const report: any[] = [];
    
    applications.forEach(app => {
      const instances = getInstances(app);
      const allUsers: any[] = [];
      
      // Collect all primary owners
      app.primary_owners?.forEach(owner => {
        const user = users.find(u => u.id === owner.user_id);
        if (user) {
          allUsers.push({ ...user, userType: 'Primary Owner' });
        }
      });
      
      // Collect all secondary owners
      app.secondary_owners?.forEach(owner => {
        const user = users.find(u => u.id === owner.user_id);
        if (user) {
          allUsers.push({ ...user, userType: 'Secondary Owner' });
        }
      });
      
      // Collect all delegates
      instances.forEach(instance => {
        if (instance.assignedTo) {
          const delegateUser = users.find(u => u.email === instance.assignedTo || `${u.first_name} ${u.last_name}` === instance.assignedTo);
          if (delegateUser) {
            allUsers.push({ ...delegateUser, userType: 'Delegate', instanceId: instance.id, instanceName: instance.name });
          }
        }
      });
      
      // Check if all users are inactive
      const activeUsers = allUsers.filter(user => user.is_active);
      if (activeUsers.length === 0 && allUsers.length > 0) {
        report.push({
          applicationId: app.id,
          applicationName: app.name,
          totalUsers: allUsers.length,
          inactiveUsers: allUsers.length,
          users: allUsers.map(user => ({
            userType: user.userType,
            userName: `${user.first_name} ${user.last_name}`,
            userEmail: user.email,
            lastLogin: 'Never',
            instanceName: user.instanceName || 'N/A'
          }))
        });
      }
    });
    
    return report;
  };

  const handleOpenReportsDialog = async () => {
    setReportsDialogOpen(true);
    setReportLoading(true);
    
    try {
      // Generate reports
      const inactiveUsers = generateInactiveUsersReport();
      const allInactive = generateAllInactiveReport();
      
      setInactiveUsersReport(inactiveUsers);
      setAllInactiveReport(allInactive);
    } catch (error) {
      console.error('Error generating reports:', error);
      setError('Failed to generate reports');
    } finally {
      setReportLoading(false);
    }
  };

  const handleCloseReportsDialog = () => {
    setReportsDialogOpen(false);
    setInactiveUsersReport([]);
    setAllInactiveReport([]);
  };

  const handleShowSSODetails = async (application: Application) => {
    console.log('🔍 handleShowSSODetails called for application:', application.name, application.id);
    try {
      const response = await ssoAPI.getConfigurationsByApplication(application.id);
      const ssoConfigs = response.data.data || [];
      console.log('🔍 Response data:', ssoConfigs);

      if (ssoConfigs.length === 0) {
        console.log('No SSO configuration found for this application');
        return;
      }

      // Prefer Completed (fully provisioned) over Approved or other statuses
      const preferredConfig =
        ssoConfigs.find((config: any) => config.status === 'Completed') ||
        ssoConfigs.find((config: any) => config.status === 'Approved') ||
        ssoConfigs[0];

      const platform = String(preferredConfig.platform || '').toLowerCase();
      const isOkta = platform === 'okta';
      const isAzure = platform === 'azure' || platform === 'entra' || platform === 'azuread';

      let oidcDiscovery =
        preferredConfig.oidc_discovery_endpoint ||
        preferredConfig.manual_config?.oidc_discovery_endpoint ||
        null;

      // Derive Okta discovery from Security connector org URL when missing
      if (isOkta && preferredConfig.sso_type === 'OIDC' && !oidcDiscovery) {
        try {
          const oktaRes = await oktaAPI.getConfig();
          const orgUrl = (oktaRes.data?.data?.org_url || '').replace(/\/$/, '');
          if (orgUrl) oidcDiscovery = `${orgUrl}/.well-known/openid-configuration`;
        } catch {
          /* non-fatal */
        }
      }

      // Only invent Azure federation URLs when IdP is Azure/Entra and config has no stored metadata
      let metadataUrl =
        preferredConfig.azure_metadata_url ||
        preferredConfig.metadata_url ||
        null;
      let idpInitiatedUrl = preferredConfig.idp_initiated_url || null;

      if (preferredConfig.sso_type === 'SAML' && !metadataUrl && isAzure) {
        // Prefer client (application) ID from provisioned Entra config — object IDs break federation URLs.
        const azureAppId =
          preferredConfig.manual_config?.entra_client_id ||
          preferredConfig.client_id ||
          preferredConfig.manual_config?.client_id ||
          null;
        const tenantId =
          preferredConfig.manual_config?.entra_tenant_id ||
          preferredConfig.tenant_id ||
          null;
        if (azureAppId && tenantId) {
          metadataUrl = buildAzureFederationMetadataUrl(tenantId, azureAppId);
          idpInitiatedUrl = idpInitiatedUrl || buildAzureIdpInitiatedUrl(azureAppId, tenantId);
        }
      }

      if (preferredConfig.sso_type === 'OIDC' && !oidcDiscovery && isAzure) {
        const tenantId =
          preferredConfig.manual_config?.entra_tenant_id ||
          preferredConfig.tenant_id ||
          null;
        if (tenantId) {
          oidcDiscovery = buildAzureOidcDiscoveryUrl(tenantId);
        }
      }

      const applicationWithUrls = {
        ...application,
        sso_type: preferredConfig.sso_type,
        platform: preferredConfig.platform,
        entity_id: preferredConfig.entity_id,
        acs_url: preferredConfig.acs_url,
        name_id_format: preferredConfig.name_id_format,
        attribute_statements: preferredConfig.attribute_statements || [],
        client_id: preferredConfig.client_id || preferredConfig.manual_config?.client_id,
        client_secret: preferredConfig.client_secret || preferredConfig.manual_config?.client_secret,
        oidc_discovery_endpoint: oidcDiscovery,
        grant_type: preferredConfig.grant_type || preferredConfig.manual_config?.grant_type || 'authorization_code',
        redirect_uris: preferredConfig.redirect_uris || preferredConfig.manual_config?.redirect_uris || [],
        scopes: preferredConfig.scopes || preferredConfig.manual_config?.scopes || [],
        azure_metadata_url: metadataUrl,
        idp_initiated_url: idpInitiatedUrl,
        certificate_expiration_date: preferredConfig.certificate_expiration_date,
        okta_app_id: preferredConfig.okta_app_id || preferredConfig.manual_config?.okta_app_id,
        entra_app_id: preferredConfig.entra_app_id || preferredConfig.manual_config?.entra_app_id,
        sso_config: preferredConfig,
        review_notes: preferredConfig.review_notes,
      };

      setSelectedAppForSSODetails(applicationWithUrls);
      setSsoDetailsSmallDialogOpen(true);
    } catch (error) {
      console.error('Error fetching SSO configuration:', error);
    }
  };

  const handleCloseSSODetails = () => {
    setSsoDetailsSmallDialogOpen(false);
    setSelectedAppForSSODetails(null);
  };

  // Handle deleting an instance
  const handleDeleteInstance = async (appId: string, instanceId: string) => {
    if (!window.confirm('Are you sure you want to delete this instance? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      console.log('Deleting instance:', { appId, instanceId });
      
      // Check if this is a virtual instance created from SSO configuration first
      const ssoConfig = ssoConfigurations.find(config => config.instance_id === instanceId);
      if (ssoConfig) {
        // This is a virtual instance from SSO configuration - delete the SSO configuration instead
        console.log('Deleting SSO configuration for virtual instance:', ssoConfig);
        await ssoAPI.deleteConfiguration(ssoConfig.id);
        setSuccess('Instance deleted successfully!');
        
        // Refresh the data to update the UI
        await loadData();
        return;
      }
      
      // Check if instance exists in real instances and user has permission
      const instance = applicationInstances[appId]?.find(inst => inst.id === instanceId);
      if (!instance) {
        setError('Instance not found');
        return;
      }
      
      console.log('Instance found:', instance);
      const app = applications.find(app => app.id === appId);
      console.log('Application found:', app);
      console.log('Application owners:', app?.primary_owners, app?.secondary_owners);
      console.log('Current user:', user);
      console.log('User can manage instances:', canManageInstances(app));
      
      // Check if this is a frontend-only instance (not in database)
      if (instance.id.startsWith('temp-') || instance.id.includes('frontend-')) {
        // Remove from frontend state only
        setApplicationInstances(prev => ({
          ...prev,
          [appId]: prev[appId]?.filter(inst => inst.id !== instanceId) || []
        }));
        setSuccess('Instance deleted successfully!');
        return;
      }
      
      // Call backend API to delete instance from database
      await instancesAPI.deleteInstance(instanceId);
      
      setSuccess('Instance deleted successfully!');
      
      // Refresh the data to update the UI
      await loadData();
    } catch (err) {
      console.error('Delete instance error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete instance';
      setError(`Failed to delete instance: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const applyRenewedSecret = (configId: string, newSecret?: string, newExpiry?: string) => {
    setSsoConfigurations((prev) =>
      prev.map((c) =>
        c.id === configId
          ? {
              ...c,
              ...(newSecret ? { client_secret: newSecret } : {}),
              ...(newExpiry ? { client_secret_expires_at: newExpiry } : {}),
              manual_config: {
                ...(c.manual_config || {}),
                ...(newSecret ? { client_secret: newSecret } : {}),
                ...(newExpiry ? { client_secret_expires_at: newExpiry } : {}),
              },
            }
          : c
      )
    );

    if (selectedAppForSSODetails && newSecret) {
      const matches =
        selectedAppForSSODetails.sso_config?.id === configId ||
        selectedAppForSSODetails.id === configId;
      if (matches) {
        setSelectedAppForSSODetails({
          ...selectedAppForSSODetails,
          client_secret: newSecret,
          sso_config: {
            ...selectedAppForSSODetails.sso_config,
            client_secret: newSecret,
            client_secret_expires_at: newExpiry,
          },
        });
      }
    }
  };

  const handleRenewedSecret = (configId: string, result: { client_secret?: string; client_secret_expires_at?: string }) => {
    applyRenewedSecret(configId, result.client_secret, result.client_secret_expires_at);
    if (oidcCredentials && result.client_secret) {
      setOidcCredentials({
        ...oidcCredentials,
        clientSecret: result.client_secret,
        ...(result.client_secret_expires_at
          ? { clientSecretExpiresAt: result.client_secret_expires_at }
          : {}),
      });
    }
    const expiryLabel = formatSecretExpiry(result.client_secret_expires_at);
    setSuccess(
      result.client_secret_expires_at
        ? `Client secret renewed. New expiry: ${expiryLabel}`
        : 'Client secret renewed successfully!'
    );
    void loadData();
  };

  const handleShowOIDCCredentials = (config: any) => {
    if (!config || config.sso_type !== 'OIDC' || !config.client_id) return;
    setOidcCredentials({
      clientId: config.client_id,
      clientSecret: config.client_secret || config.manual_config?.client_secret,
      oidcDiscoveryEndpoint:
        config.oidc_discovery_endpoint ||
        config.manual_config?.oidc_discovery_endpoint ||
        '',
      clientSecretExpiresAt:
        config.client_secret_expires_at || config.manual_config?.client_secret_expires_at,
      grantType: (config.grant_type ||
        config.manual_config?.grant_type ||
        'authorization_code') as 'authorization_code' | 'client_credentials' | 'authorization_code_pkce',
      connectionName: config.connection_name || config.application_name || 'OIDC Connection',
    });
    setOidcDialogOpen(true);
  };

  // Load applications from a configured identity provider
  const refreshConfiguredImportIdps = useCallback(async () => {
    try {
      const response = await ssoAPI.getConfiguredImportIdps();
      const options = response.data.data?.options || [];
      setConfiguredImportIdps(options);
      return options;
    } catch {
      setConfiguredImportIdps([]);
      return [];
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      void refreshConfiguredImportIdps();
    }
  }, [isSuperAdmin, refreshConfiguredImportIdps]);

  const loadIdpApplications = async (idp: string) => {
    try {
      setImportIdpLoading(true);
      setImportIdpError(null);
      const response =
        idp === 'okta' ? await ssoAPI.fetchOktaApplications() : await ssoAPI.fetchAzureApplications();
      const body = response.data;
      if (!body.success) {
        setImportIdpError(body.error || `Failed to load ${idp === 'okta' ? 'Okta' : 'Azure'} applications`);
        setAvailableApplications([]);
        return;
      }
      const apps = body.data || [];
      setAvailableApplications(apps);
      if (apps.length === 0) {
        setImportIdpError(
          `No SSO-capable applications found in ${idp === 'okta' ? 'Okta' : 'Azure Entra ID'}.`
        );
      }
    } catch (error: any) {
      console.error('Error loading IdP applications:', error);
      setImportIdpError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Failed to load applications from identity provider'
      );
      setAvailableApplications([]);
    } finally {
      setImportIdpLoading(false);
    }
  };

  const openImportIdpDialog = async () => {
    setImportIdpDialogOpen(true);
    setImportIdpError(null);
    setSelectedApplications([]);
    setAvailableApplications([]);
    setSelectedIdp('');

    const options = await refreshConfiguredImportIdps();
    if (options.length === 0) {
      setImportIdpError('No identity providers are configured for this tenant. Set up Azure Entra ID or Okta under Security settings.');
      return;
    }

    const firstIdp = options[0].id;
    setSelectedIdp(firstIdp);
    await loadIdpApplications(firstIdp);
  };

  const handleIdpSourceChange = async (idp: string) => {
    if (!configuredImportIdps.some((opt) => opt.id === idp)) return;
    setSelectedIdp(idp);
    setSelectedApplications([]);
    setAvailableApplications([]);
    setImportIdpError(null);
    await loadIdpApplications(idp);
  };

  const toggleImportAppSelection = (appId: string, checked: boolean) => {
    setSelectedApplications((prev) =>
      checked ? [...prev, appId] : prev.filter((id) => id !== appId)
    );
  };

  const getImportRowSx = (app: { id: string; hasUnmatchedOwners?: boolean }) => {
    const selected = selectedApplications.includes(app.id);
    return {
      cursor: 'pointer',
      bgcolor: selected ? 'primary.50' : 'background.paper',
      borderLeft: selected ? '3px solid' : '3px solid transparent',
      borderLeftColor: selected ? 'primary.main' : 'transparent',
      ...(app.hasUnmatchedOwners
        ? { bgcolor: selected ? 'warning.50' : 'grey.50' }
        : {}),
      '&:hover': { bgcolor: selected ? 'primary.100' : 'action.hover' },
    };
  };

  // Handle importing selected applications
  const handleImportApplications = async () => {
    if (selectedApplications.length === 0) return;
    
    try {
      setImportIdpLoading(true);
      
      // Filter selected applications
      const appsToImport = availableApplications.filter(app => selectedApplications.includes(app.id));
      
      if (selectedIdp === 'azure') {
        const response = await ssoAPI.importAzureApplications(appsToImport);
        const result = response.data.data;
        setSuccess(`Successfully imported ${result.imported_count} applications with owner assignment`);
      } else if (selectedIdp === 'okta') {
        const response = await ssoAPI.importOktaApplications(appsToImport);
        const result = response.data.data;
        setSuccess(`Successfully imported ${result.imported_count} applications from Okta`);
      } else {
        // For other IDPs, fall back to the old method
        const externalApp = applications.find(app => app.name === 'External Applications');
        if (!externalApp) {
          setError('External Applications app not found. Please contact administrator.');
          return;
        }
        
        // Create instances for each selected application
        for (const app of appsToImport) {
          try {
            // Create instance data
            const instanceData = {
              application_id: externalApp.id,
              name: app.displayName || 'Unknown App',
              description: `Imported from ${selectedIdp.toUpperCase()}`,
              environment: 'production',
              sso_type: detectSSOType(app),
              external_app_id: app.appId,
              external_app_name: app.displayName,
              owner_emails: app.ownerEmails || []
            };

            // Create the instance
            await instancesAPI.createInstance(instanceData);
            
            // If there are owners, try to assign them
            if (app.ownerEmails && app.ownerEmails.length > 0) {
              // This would need to be implemented in the backend
              // For now, we'll just log the owners
              console.log(`Owners for ${app.displayName}:`, app.ownerEmails);
            }
          } catch (error) {
            console.error(`Error importing ${app.displayName}:`, error);
          }
        }
        
        setSuccess(`Successfully imported ${appsToImport.length} applications`);
      }

      setImportIdpDialogOpen(false);
      setSelectedApplications([]);
      setAvailableApplications([]);
      setSelectedIdp('');
      setImportIdpError(null);
      
      // Reload data to show the new instances
      await loadData();
      
    } catch (error: any) {
      console.error('Error importing applications:', error);
      setError(error.response?.data?.message || 'Failed to import applications');
    } finally {
      setImportIdpLoading(false);
    }
  };

  // Detect SSO type (SAML vs OIDC) for Azure / Okta applications
  const detectSSOType = (app: any): string => {
    if (app.ssoType) return app.ssoType;
    if (app.signOnMode) {
      const mode = String(app.signOnMode).toUpperCase();
      if (mode.includes('SAML')) return 'SAML';
      if (mode.includes('OPENID') || mode.includes('OIDC')) return 'OIDC';
    }
    
    // Check for SAML-specific tags (most reliable for SAML detection)
    const hasSamlTags = app.tags && app.tags.some((tag: string) => 
      tag === 'WindowsAzureActiveDirectoryCustomSingleSignOnApplication' ||
      tag === 'WindowsAzureActiveDirectoryIntegratedApp' ||
      tag.includes('WindowsAzureActiveDirectory') ||
      tag.includes('SAML') ||
      tag.toLowerCase().includes('saml')
    );
    
    // Check for SAML Single Sign-On Settings
    const hasSamlSingleSignOnSettings = app.samlSingleSignOnSettings && 
      (app.samlSingleSignOnSettings.relayState || app.samlSingleSignOnSettings.uri);
    
    // Check for SAML metadata URL
    const hasSamlMetadata = app.samlMetadataUrl;
    
    // Check for SAML-specific reply URLs (ACS URLs)
    const hasSamlReplyUrls = app.replyUrls && app.replyUrls.some((url: string) => 
      url.includes('/saml') || url.includes('/acs') || url.includes('/sso')
    );
    
    // Check for SAML-specific homepage URL patterns
    const hasSamlHomepage = app.web && app.web.homePageUrl && 
      (app.web.homePageUrl.includes('saml') || app.web.homePageUrl.includes('acs'));
    
    // If any SAML indicators are found, it's SAML
    if (hasSamlTags || hasSamlSingleSignOnSettings || hasSamlMetadata || hasSamlReplyUrls || hasSamlHomepage) {
      return 'SAML';
    }
    
    // Check for OIDC indicators
    const hasOidcScopes = app.api && app.api.oauth2PermissionScopes && app.api.oauth2PermissionScopes.length > 0;
    const hasOidcRedirectUris = app.web && app.web.redirectUris && app.web.redirectUris.length > 0;
    const hasOidcAppRoles = app.appRoles && app.appRoles.length > 0;
    
    // If it has OIDC indicators, it's OIDC
    if (hasOidcScopes || hasOidcRedirectUris || hasOidcAppRoles) {
      return 'OIDC';
    }
    
    // Default to OIDC for modern applications
    return 'OIDC';
  };

  // Track onboarding submissions for each application instance (no longer using localStorage)
  const [onboardingSubmissions, setOnboardingSubmissions] = useState<{[key: string]: {
    instances: {
      [instanceId: string]: {
        igaStatus: string;
        ssoStatus: string;
        pamStatus: string;
      }
    }
  }}>({});

  // No longer saving to localStorage - all data comes from database

  // Load available attributes when SSO dialog opens
  useEffect(() => {
    if (viewSSODialogOpen) {
      loadAvailableAttributes();
    }
  }, [viewSSODialogOpen]);

  // Get the SSO status for a specific instance
  const getInstanceSSOStatus = (appId: string, instanceId: string): string => {
    // First check if there's an actual SSO configuration in the database (loaded via loadSsoConfigurations)
    const config = ssoConfigurations.find(c =>
      c.application_id === appId &&
      c.instance_id === instanceId
    );
    if (config) return config.status;

    // Fallback to local onboarding submissions state
    const appSubmissions = onboardingSubmissions[appId];
    const submissionStatus = appSubmissions?.instances?.[instanceId]?.ssoStatus;
    if (submissionStatus && submissionStatus !== 'Not Started') return submissionStatus;

    // Final fallback: use the DB-sourced sso_status from applicationInstances
    // (populated directly from app_instances.sso_status on page load)
    const dbInstance = (applicationInstances[appId] || []).find(i => i.id === instanceId);
    if (dbInstance?.ssoStatus && dbInstance.ssoStatus !== 'Not Started') return dbInstance.ssoStatus;

    return 'Not Started';
  };

  // Get the SSO assignment for a specific instance
  const getInstanceSSOAssignment = (appId: string, instanceId: string): string => {
    // Find SSO configuration for this application and instance
    const config = ssoConfigurations.find(c => 
      c.application_id === appId && 
      c.instance_id === instanceId
    );
    return config?.assigned_to_name || 'Unassigned';
  };

  // Get the SSO type for a specific instance
  const getInstanceSSOType = (appId: string, instanceId: string): string => {
    // Find SSO configuration for this application and instance
    const config = ssoConfigurations.find(c => 
      c.application_id === appId && 
      c.instance_id === instanceId
    );
    return config?.sso_type || 'N/A';
  };

  // Handle viewing SSO configuration
  const handleViewSSO = async (appId: string, instanceId?: string) => {
    try {
      // Check if user has permission to view SSO configurations
      const app = applications.find(app => app.id === appId);
      if (!isApplicationOwner(app) && !isSuperAdmin && !isSSOAdmin) {
        setError('You do not have permission to view SSO configurations for this application');
        return;
      }
      
      const response = await ssoAPI.getConfigurationsByApplication(appId);
      const configs = response.data.data || [];
      
      if (configs && configs.length > 0) {
        // If instanceId is provided, find the specific configuration for that instance
        let targetConfig: any;
        if (instanceId) {
          targetConfig = configs.find(config => config.instance_id === instanceId);
        }
        
        // If no specific instance config found, get the latest configuration
        if (!targetConfig) {
          targetConfig = configs.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )[0];
        }
        
        setSelectedSSOConfig(targetConfig);
        setViewSSODialogOpen(true);
        
        // Load notes for this SSO configuration
        try {
          const response = await ssoAPI.getNotes(targetConfig.id);
          setSsoNotes(response.data.data || []);
        } catch (err) {
          console.error('Error loading SSO notes:', err);
          setSsoNotes([]);
        }
        
        // Update local state to reflect the current status for the specific instance
        setOnboardingSubmissions(prev => {
          const newState = { ...prev };
          if (!newState[appId]) {
            newState[appId] = { instances: {} };
          }
          
          // Update only the specific instance that was configured
          if (targetConfig.instance_id) {
            newState[appId].instances[targetConfig.instance_id] = {
              ...newState[appId].instances[targetConfig.instance_id],
              ssoStatus: targetConfig.status
            };
          }
          
          return newState;
        });
      } else {
        setError('No SSO configuration found for this application');
      }
    } catch (err) {
      setError('Failed to load SSO configuration');
    }
  };

  // Handle viewing notes for an application instance
  const handleViewNotes = async (appId: string, instanceId: string) => {
    try {
      const app = applications.find(app => app.id === appId);
      if (!app) return;

      setSelectedAppForNotes(app);
      setSelectedInstanceForNotes(instanceId);
      
      // Get SSO configurations for this application
      const response = await ssoAPI.getConfigurationsByApplication(appId);
      const configs = response.data.data || [];
      
      // Find the configuration for this specific instance
      const instanceConfig = configs?.find(config => config.instance_id === instanceId);
      
      if (instanceConfig) {
        // Load notes for this SSO configuration
        const notesResponse = await ssoAPI.getNotes(instanceConfig.id);
        setInstanceNotes(notesResponse.data.data || []);
        
        // Mark comments as viewed for this instance
        setLastViewedComments(prev => ({
          ...prev,
          [instanceId]: new Date().toISOString()
        }));
        
        // Update unread counts
        await updateUnreadCommentCounts();
      } else {
        setInstanceNotes([]);
      }
      
      setNotesDialogOpen(true);
    } catch (err) {
      console.error('Error loading notes:', err);
      setError('Failed to load notes');
      setInstanceNotes([]);
    }
  };

  // Load available attributes for SSO edit dialog
  const loadAvailableAttributes = async () => {
    try {
      const res = await oktaAPI.listSsoAttributes('application');
      setAvailableAttributes(res.data?.data || []);
    } catch (error) {
      console.error('Error loading attributes:', error);
      setAvailableAttributes([]);
    }
  };

  // Handle attribute checkbox changes
  const handleAttributeChange = (attributeId: string, checked: boolean) => {
    setSelectedAttributes(prev => {
      const newSelection = checked 
        ? [...prev, attributeId]
        : prev.filter(id => id !== attributeId);
      
      // Update the form data as well
      setSsoEditFormData((prevForm: any) => ({
        ...prevForm,
        attributes_to_release: newSelection
      }));
      
      return newSelection;
    });
  };

  // Handle starting SSO configuration edit mode
  const handleStartEditSSO = () => {
    if (selectedSSOConfig) {
      const attributes = selectedSSOConfig.attributes_to_release || [];
      setSelectedAttributes(attributes);
      
      setSsoEditFormData({
        sso_type: selectedSSOConfig.sso_type || 'SAML',
        grant_type: selectedSSOConfig.grant_type || 'authorization_code_pkce',
        entity_id: selectedSSOConfig.entity_id || '',
        acs_url: selectedSSOConfig.acs_url || '',
        name_id_format: selectedSSOConfig.name_id_format || '',
        x509_certificate: selectedSSOConfig.x509_certificate || '',
        private_key: selectedSSOConfig.private_key || '',
        client_id: selectedSSOConfig.client_id || '',
        client_secret: selectedSSOConfig.client_secret || '',
        redirect_uris: selectedSSOConfig.redirect_uris || [],
        scopes: selectedSSOConfig.scopes || [],
        connection_name: selectedSSOConfig.connection_name || '',
        contact_email: selectedSSOConfig.contact_email || '',
        metadata_url: selectedSSOConfig.metadata_url || '',
        metadata_xml: selectedSSOConfig.metadata_xml || '',
        attributes_to_release: attributes
      });
      setIsEditingSSO(true);
    }
  };

  // Handle saving SSO configuration changes
  const handleSaveSSOEdit = async () => {
    if (!selectedSSOConfig) return;

    try {
      setLoading(true);
      await ssoAPI.updateConfiguration(selectedSSOConfig.id, ssoEditFormData);
      setSuccess('SSO configuration updated successfully!');
      setIsEditingSSO(false);
      
      // Refresh the configuration data
      const response = await ssoAPI.getConfigurationById(selectedSSOConfig.id);
      setSelectedSSOConfig(response.data.data);
      
      // Refresh the main data
      await loadData();
    } catch (err) {
      console.error('Error updating SSO configuration:', err);
      setError(`Failed to update SSO configuration: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle canceling SSO configuration edit
  const handleCancelSSOEdit = () => {
    setIsEditingSSO(false);
    setSsoEditFormData({});
    setSelectedAttributes([]);
  };

  // Handle SSO type change in edit mode
  const handleSSOTypeChange = (newSSOType: string) => {
    setSsoEditFormData((prev: any) => ({
      ...prev,
      sso_type: newSSOType,
      // Reset type-specific fields when switching types
      entity_id: newSSOType === 'SAML' ? (prev.entity_id || '') : '',
      acs_url: newSSOType === 'SAML' ? (prev.acs_url || '') : '',
      name_id_format: newSSOType === 'SAML' ? (prev.name_id_format || '') : '',
      x509_certificate: newSSOType === 'SAML' ? (prev.x509_certificate || '') : '',
      private_key: newSSOType === 'SAML' ? (prev.private_key || '') : '',
      client_id: newSSOType === 'OIDC' ? (prev.client_id || '') : '',
      client_secret: newSSOType === 'OIDC' ? (prev.client_secret || '') : '',
      redirect_uris: newSSOType === 'OIDC' ? (prev.redirect_uris || []) : [],
      scopes: newSSOType === 'OIDC' ? (prev.scopes || []) : [],
      grant_type: newSSOType === 'OIDC' ? (prev.grant_type || 'authorization_code_pkce') : ''
    }));
  };

  // Handle adding a new note to SSO configuration
  const handleAddSSONote = async () => {
    if (!selectedSSOConfig || !newNote.trim()) return;

    try {
      await ssoAPI.addNote(selectedSSOConfig.id, {
        note_type: 'comment',
        content: newNote.trim(),
        is_internal: false
      });
      
      // Reload notes
      const response = await ssoAPI.getNotes(selectedSSOConfig.id);
      setSsoNotes(response.data.data || []);
      setNewNote('');
      setSuccess('Note added successfully!');
      
      // Show notification to other users
      showSuccess('Comment added successfully');
      showInfo('Other users will be notified of this new comment');
      
      // Refresh SSO configurations to get latest data
      loadSsoConfigurations();
    } catch (err) {
      console.error('Error adding SSO note:', err);
      setError('Failed to add note');
    }
  };

  // Show notification
  const showNotificationMessage = (message: string, type: 'success' | 'info' | 'warning' = 'success') => {
    const notification = {
      id: Date.now(),
      message,
      type,
      timestamp: new Date()
    };
    
    setNotifications(prev => [...prev, notification]);
    setShowNotification(true);
    
    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      setShowNotification(false);
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  // Handle adding a new note to instance from the notes dialog
  const handleAddNoteToInstance = async () => {
    if (!selectedAppForNotes || !selectedInstanceForNotes || !newNote.trim()) return;

    try {
      // Get SSO configurations for this application
      const response = await ssoAPI.getConfigurationsByApplication(selectedAppForNotes.id);
      const configs = response.data.data || [];
      
      // Find the configuration for this specific instance
      const instanceConfig = configs?.find(config => config.instance_id === selectedInstanceForNotes);
      
      if (!instanceConfig) {
        setError('No SSO configuration found for this instance');
        return;
      }

      await ssoAPI.addNote(instanceConfig.id, {
        note_type: 'comment',
        content: newNote.trim(),
        is_internal: false
      });
      
      // Reload notes for this instance
      const notesResponse = await ssoAPI.getNotes(instanceConfig.id);
      setInstanceNotes(notesResponse.data.data || []);
      setNewNote('');
      
      setSuccess('Note added successfully');
      showNotificationMessage(`New comment added to ${selectedAppForNotes.name} - ${selectedInstanceForNotes}`, 'info');
      
      // Show notification to other users
      showSuccess('Comment added successfully');
      showInfo('Other users will be notified of this new comment');
      
      // Refresh SSO configurations to get latest data
      loadSsoConfigurations();
    } catch (err) {
      console.error('Error adding note:', err);
      setError('Failed to add note');
    }
  };

  // Get instances (real instances from database + any created instances)
  const getInstances = (app: Application): AppInstance[] => {
    // Add null check for app parameter
    if (!app) {
      return [];
    }
    
    console.log('🔍 getInstances for app:', app.name, app.id);
    console.log('📦 applicationInstances:', applicationInstances);
    console.log('📦 applicationInstances for this app:', applicationInstances[app.id]);
    console.log('📦 createdInstances:', createdInstances);
    console.log('📦 createdInstances for this app:', createdInstances[app.id]);
    
    // Start with real instances from database
    const realInstances: AppInstance[] = applicationInstances[app.id] || [];

    // Add instances from SSO configurations (excluding Main instances and existing real instances)
    // Only show virtual instances to admins, not regular users
    const ssoConfigInstances: AppInstance[] = [];
    if (isSuperAdmin || isSSOAdmin) {
      const appSSOConfigs = (ssoConfigurations || [])
        .filter(config => config && config.application_id === app.id);
      
      console.log('🔍 SSO configs for app', app.name, ':', appSSOConfigs.length, appSSOConfigs);
      
      appSSOConfigs.forEach(config => {
        // Add SSO configuration instances, but exclude Main instances and existing real instances
        if (config.instance_id && !config.instance_id.includes('-main')) {
          // Check if this instance already exists in real instances
          const existingInstance = realInstances.find(inst => inst.id === config.instance_id);
          
          console.log('🔍 Processing SSO config:', {
            instanceId: config.instance_id,
            existingInstance: !!existingInstance,
            configStatus: config.status
          });
          
        if (!existingInstance) {
          // Extract environment from instance_id (e.g., "1756783406048-uat" -> "UAT")
          const environment = config.instance_id.includes('-uat') ? 'UAT' : 
                             config.instance_id.includes('-staging') ? 'Staging' :
                             config.instance_id.includes('-prod') ? 'Production' : 'Other';
          
          ssoConfigInstances.push({
            id: config.instance_id,
                application_id: app.id,
                name: app.name,
            environment: environment,
            description: `${environment} environment for ${app.name}`,
              iga_status: 'Not Started',
              sso_status: config.status || 'Not Started',
              pam_status: 'Not Started',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              // Legacy camelCase properties for backward compatibility
            primaryOwner: app.primary_owners?.[0]?.first_name + ' ' + app.primary_owners?.[0]?.last_name || 'Unknown',
            secondaryOwners: app.secondary_owners?.map(owner => owner.first_name + ' ' + owner.last_name) || [],
            igaStatus: 'Not Started',
            ssoStatus: config.status,
            pamStatus: 'Not Started',
            assignedTo: config.assigned_to_name || '',
            ssoType: config.sso_type,
            delegatedTo: '',
            businessJustification: '',
            expectedUserCount: 0,
            dataClassification: '',
            delegationNotes: ''
          });
          }
        }
      });
    }

    // Add any created instances for this app (excluding Main instances)
    const createdAppInstances = (createdInstances[app.id] || []).filter(instance => 
      instance.environment !== 'Main'
    );
    
    // Update SSO status and owners for created instances
    const updatedCreatedInstances = createdAppInstances.map(instance => ({
      ...instance,
      primaryOwner: app.primary_owners?.[0]?.first_name && app.primary_owners?.[0]?.last_name 
        ? `${app.primary_owners[0].first_name} ${app.primary_owners[0].last_name}`
        : instance.primaryOwner || 'To be assigned',
      secondaryOwners: app.secondary_owners?.map(owner => `${owner.first_name} ${owner.last_name}`) || instance.secondaryOwners || [],
      ssoStatus: getInstanceSSOStatus(app.id, instance.id),
      assignedTo: getInstanceSSOAssignment(app.id, instance.id),
      ssoType: getInstanceSSOType(app.id, instance.id)
    }));

    // Update SSO status for real instances
    const updatedRealInstances = realInstances.map(instance => ({
      ...instance,
      ssoStatus: getInstanceSSOStatus(app.id, instance.id),
      assignedTo: getInstanceSSOAssignment(app.id, instance.id),
      ssoType: getInstanceSSOType(app.id, instance.id)
    }));

    // Combine all instances and deduplicate by ID
    const allInstances = [...updatedRealInstances, ...ssoConfigInstances, ...updatedCreatedInstances];
    
    // Deduplicate instances by ID, prioritizing real instances over virtual ones
    const uniqueInstances = allInstances.reduce((acc, instance) => {
      const existingIndex = acc.findIndex(existing => existing.id === instance.id);
      if (existingIndex === -1) {
        acc.push(instance);
      } else {
        // If we find a duplicate, prioritize real instances over virtual ones
        const existing = acc[existingIndex];
        const isCurrentReal = realInstances.some(real => real.id === instance.id);
        const isExistingReal = realInstances.some(real => real.id === existing.id);
        
        if (isCurrentReal && !isExistingReal) {
          // Replace virtual with real instance
          acc[existingIndex] = instance;
        }
        // Otherwise keep the existing instance (real instances have priority)
      }
      return acc;
    }, [] as AppInstance[]);
    
    console.log('🎯 Final instances for app:', app.name, uniqueInstances);
    return uniqueInstances;
  };

  // Calculate application statistics
  const getApplicationStats = () => {
    const totalApps = applications.length;
    
    // Calculate total instances by counting all instances for each app
    const totalInstances = applications.reduce((sum, app) => {
      const instances = getInstances(app);
      return sum + instances.length;
    }, 0);
    
    // Count unique apps with SSO configurations
    const appsWithSSO = applications.filter(app => {
      const appSSOStatus = getApplicationSSOStatus(app.id);
      return appSSOStatus && appSSOStatus !== 'Not Started';
    }).length;
    
    // Count instances with SSO configurations
    const instancesWithSSO = applications.reduce((sum, app) => {
      const appSSOStatus = getApplicationSSOStatus(app.id);
      if (appSSOStatus && appSSOStatus !== 'Not Started') {
        const instances = getInstances(app);
        return sum + instances.length;
      }
      return sum;
    }, 0);
    
    return {
      totalApps,
      totalInstances,
      appsWithSSO,
      instancesWithSSO,
      appOnboardingPercentage: totalApps > 0 ? Math.round((appsWithSSO / totalApps) * 100) : 0,
      instanceOnboardingPercentage: totalInstances > 0 ? Math.round((instancesWithSSO / totalInstances) * 100) : 0
    };
  };

  const stats = getApplicationStats();

  const renderApplicationCard = (app: Application, cardIndex: number = 0) => {
    const ACCENT_COLORS = ['#1976d2', '#7b1fa2', '#00695c', '#c62828', '#e65100', '#1565c0', '#4a148c', '#00838f'];
    const accentColor = ACCENT_COLORS[cardIndex % ACCENT_COLORS.length];
    const instances = getInstances(app);
    const searchMatchInfo = getSearchMatchInfo(app);
    const riskApproved = isRiskApproved(app.id, app.risk_level);
    const isOwner = isApplicationOwner(app);
    // Show GRC-approved risk level, or admin-set risk level, or "Not Assessed"
    // app.risk_level is always authoritative — assessments[].risk_level is only a supplementary label
    const displayRiskLevel = ['Low', 'Medium', 'High', 'Critical'].includes(app.risk_level || '')
      ? app.risk_level
      : 'Not Assessed';
    const riskStyle = getRiskChipStyle(displayRiskLevel);

    // SSO onboarding action chip/button for an instance row
    const renderSSOAction = (instance: AppInstance) => {
      const sso = instance.ssoStatus || 'Not Started';
      if (sso === 'Not Started') {
        if (canManageInstances(app) || canOnboardInstances(app) || isSSOAdmin) {
          return (
            <Tooltip title="Onboard this instance to SSO">
              <Button
                size="small" variant="outlined" color="primary"
                startIcon={<SecurityIcon sx={{ fontSize: `${UI_TOKENS.text.buttonSm} !important` }} />}
                onClick={() => handleOnboard(app.id, 'SSO', instance.id)}
                sx={{ fontSize: UI_TOKENS.text.labelXs, height: 22, px: 1, py: 0, borderRadius: 1.5, lineHeight: 1 }}
              >
                Onboard SSO
              </Button>
            </Tooltip>
          );
        }
        return (
          <Chip label="SSO: Not Started" size="small" variant="outlined"
            sx={{ height: 20, fontSize: UI_TOKENS.text.helper, color: 'text.disabled', borderColor: 'divider', '& .MuiChip-label': { px: 0.75 } }} />
        );
      }
      const ssoLower = sso.toLowerCase();
      if (['submitted', 'pending', 'in review', 'under review', 'review'].some(s => ssoLower.includes(s))) {
        return (
          <Chip icon={<PendingIcon sx={{ fontSize: `${UI_TOKENS.text.buttonSm} !important` }} />} label="SSO: In Review"
            size="small" color="info" variant="outlined"
            sx={{ height: 20, fontSize: UI_TOKENS.text.helper, cursor: 'pointer', '& .MuiChip-label': { px: 0.75 } }}
            onClick={() => handleViewSSO(app.id, instance.id)} />
        );
      }
      if (ssoLower.includes('need') || ssoLower.includes('more info') || ssoLower.includes('reject')) {
        return (
          <Chip icon={<WarningIcon sx={{ fontSize: `${UI_TOKENS.text.buttonSm} !important` }} />} label="SSO: Need Info"
            size="small" color="warning" variant="outlined"
            sx={{ height: 20, fontSize: UI_TOKENS.text.helper, cursor: 'pointer', '& .MuiChip-label': { px: 0.75 } }}
            onClick={() => handleViewSSO(app.id, instance.id)} />
        );
      }
      if (['approved', 'active', 'completed', 'configured', 'enabled'].some(s => ssoLower.includes(s))) {
        return (
          <Chip icon={<CheckCircleIcon sx={{ fontSize: `${UI_TOKENS.text.buttonSm} !important` }} />} label="SSO: Active"
            size="small" color="success" variant="outlined"
            sx={{ height: 20, fontSize: UI_TOKENS.text.helper, cursor: 'pointer', '& .MuiChip-label': { px: 0.75 } }}
            onClick={() => handleViewSSO(app.id, instance.id)} />
        );
      }
      return (
        <Chip label={`SSO: ${sso}`} size="small" variant="outlined"
          sx={{ height: 20, fontSize: UI_TOKENS.text.helper, '& .MuiChip-label': { px: 0.75 } }} />
      );
    };
    
    return (
      <Card
        key={app.id}
        id={`app-${app.id}`}
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          border: '1px solid',
          borderColor: 'divider',
          borderLeft: `4px solid ${accentColor}`,
          overflow: 'hidden',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' }
        }}
      >
        <CardContent sx={{ p: 0, pb: '0 !important' }}>
          {/* ── Single header row: name · meta · actions ── */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: `${accentColor}08`, borderBottom: '1px solid', borderColor: 'divider' }}>
          {(() => {
            const riskSurface = getRiskSurface(displayRiskLevel, resolvedMode);
            const riskColor = riskSurface.fg;
            const riskBg = riskSurface.bg;
            const activeSurface = getStatusSurface('success', resolvedMode);
            const neutralSurface = getStatusSurface('neutral', resolvedMode);
            const reviewSurface = getStatusSurface('info', resolvedMode);
            const warningSurface = getStatusSurface('warning', resolvedMode);
            const dot = <Box component="span" sx={{ mx: 0.75, color: 'text.disabled', userSelect: 'none' }}>·</Box>;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Name + info icon */}
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: accentColor, lineHeight: 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {app.name}
                </Typography>
                <Tooltip placement="bottom-start" arrow title={
                  <Box sx={{ p: 0.5 }}>
                    {app.description && <Typography variant="caption" sx={{ display: 'block', mb: 0.75, lineHeight: 1.5 }}>{app.description}</Typography>}
                    {app.business_function && (
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 0.4 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>Function:</Typography>
                        <Typography variant="caption">{app.business_function}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>App ID:</Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{app.application_id || '—'}</Typography>
                    </Box>
                  </Box>
                }>
                  <InfoOutlinedIcon sx={{ fontSize: '0.85rem', color: 'text.disabled', flexShrink: 0, cursor: 'default', '&:hover': { color: 'primary.main' } }} />
                </Tooltip>
                {searchMatchInfo.hasMatch && (
                  <Tooltip title={`Found in: ${searchMatchInfo.matchedFields.join(', ')}`} arrow placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'success.50', color: 'success.main', borderRadius: '50%', width: 16, height: 16, justifyContent: 'center', flexShrink: 0 }}>
                      <SearchIcon sx={{ fontSize: UI_TOKENS.text.helper }} />
                    </Box>
                  </Tooltip>
                )}

                {/* Divider between name and meta */}
                <Box sx={{ width: '1px', height: 18, bgcolor: 'divider', flexShrink: 0 }} />

                {/* Inline metadata */}
                {(() => {
                  const K = ({ children }: { children: React.ReactNode }) => (
                    <Typography component="span" sx={{ fontSize: UI_TOKENS.text.helper, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.3, mr: 0.4, whiteSpace: 'nowrap' }}>{children}</Typography>
                  );
                  const chipSx = { height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600, '& .MuiChip-label': { px: 1 } };
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', overflow: 'hidden', minWidth: 0, flex: 1, gap: 0 }}>
                      {/* Owner */}
                      <Tooltip title={
                        <Box sx={{ p: 0.5 }}>
                          {app.primary_owners?.map((o, i) => <Typography key={i} variant="caption" sx={{ display: 'block' }}>{o.first_name} {o.last_name} — {o.email}</Typography>)}
                          {(app.secondary_owners?.length ?? 0) > 0 && <><Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700 }}>Secondary:</Typography>{app.secondary_owners?.map((o, i) => <Typography key={i} variant="caption" sx={{ display: 'block' }}>{o.first_name} {o.last_name}</Typography>)}</>}
                        </Box>
                      }>
                        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'default', flexShrink: 0 }}>
                          <K>Owner</K>
                          <Typography component="span" sx={{ fontSize: UI_TOKENS.text.caption, color: 'text.primary', whiteSpace: 'nowrap' }}>
                            {app.primary_owners?.[0] ? `${app.primary_owners[0].first_name} ${app.primary_owners[0].last_name}` : 'Unassigned'}
                            {(app.primary_owners?.length ?? 0) > 1 && <span style={{ color: '#bdbdbd', fontSize: '0.68rem' }}> +{app.primary_owners!.length - 1}</span>}
                          </Typography>
                        </Box>
                      </Tooltip>

                      {dot}

                      {/* Risk */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <K>Risk</K>
                        <Chip label={displayRiskLevel} size="small" sx={{ ...chipSx, backgroundColor: riskBg, color: riskColor }} />
                      </Box>

                      {dot}

                      {/* Status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <K>Status</K>
                        <Chip label={app.status} size="small" sx={{ ...chipSx, backgroundColor: app.status === 'Active' ? activeSurface.bg : neutralSurface.bg, color: app.status === 'Active' ? activeSurface.fg : neutralSurface.fg, border: `1px solid ${app.status === 'Active' ? activeSurface.border : neutralSurface.border}` }} />
                      </Box>

                      {dot}

                      {/* Assessment */}
                      {(() => {
                        const assessment = appAssessments[app.id];
                        const assessmentStatus = assessment?.status;
                        const riskLevelApproved = ['Low', 'Medium', 'High', 'Critical'].includes(app.risk_level || '');
                        // Live assessment status always takes priority. Only fall back to
                        // app.risk_level-based "Approved" when there is no in-progress assessment
                        // (re-assessed apps with a pending status must NOT show stale "Approved").
                        const effectiveStatus = (assessmentStatus && assessmentStatus !== 'Approved')
                          ? assessmentStatus
                          : assessmentStatus === 'Approved' || (riskLevelApproved && app.risk_level !== 'Not Defined')
                            ? 'Approved'
                            : (app.risk_level === 'In Review' ? 'In Review'
                              : app.risk_level === 'Needs More Information' ? 'Needs More Information'
                              : null);

                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <K>Assessment</K>
                            {effectiveStatus === 'Approved' ? (
                              <>
                                <Chip label="Approved" size="small" sx={{ ...chipSx, backgroundColor: activeSurface.bg, color: activeSurface.fg, border: `1px solid ${activeSurface.border}` }} />
                                {(isSuperAdmin || user?.role === 'grc_admin') && assessment?.calculated_risk_score != null && (
                                  <Typography component="span" sx={{ fontSize: UI_TOKENS.text.labelXs, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                    Score: <strong style={{ color: 'text.primary' }}>{assessment.calculated_risk_score}</strong>
                                  </Typography>
                                )}
                              </>
                            ) : effectiveStatus === 'Submitted' || effectiveStatus === 'In Review' ? (
                              <Chip label="In Review" size="small" sx={{ ...chipSx, backgroundColor: reviewSurface.bg, color: reviewSurface.fg, border: `1px solid ${reviewSurface.border}` }} />
                            ) : effectiveStatus === 'Needs More Information' || effectiveStatus === 'Clarification Needed' ? (
                              (isApplicationOwner(app) || isSuperAdmin)
                                ? <Button size="small" variant="outlined" color="warning" onClick={() => handleOpenRiskAssessment(app)} sx={{ fontSize: '0.68rem', fontWeight: 700, py: 0.25, px: 1, height: 20, minWidth: 0, lineHeight: 1, boxShadow: 'none', borderRadius: 1 }}>More Info Needed</Button>
                                : <Chip label="More Info Needed" size="small" sx={{ ...chipSx, backgroundColor: warningSurface.bg, color: warningSurface.fg, border: `1px solid ${warningSurface.border}` }} />
                            ) : effectiveStatus === 'Draft' ? (
                              (isApplicationOwner(app) || isSuperAdmin)
                                ? <Button size="small" variant="contained" color="warning" onClick={() => handleOpenRiskAssessment(app)} sx={{ fontSize: '0.68rem', fontWeight: 700, py: 0.25, px: 1, height: 20, minWidth: 0, lineHeight: 1, boxShadow: 'none', borderRadius: 1 }}>Continue</Button>
                                : <Chip label="Draft" size="small" sx={{ ...chipSx, backgroundColor: neutralSurface.bg, color: neutralSurface.fg, border: `1px solid ${neutralSurface.border}` }} />
                            ) : (
                              (isApplicationOwner(app) || isSuperAdmin)
                                ? <Button size="small" variant="contained" color="warning" onClick={() => handleOpenRiskAssessment(app)} sx={{ fontSize: '0.68rem', fontWeight: 700, py: 0.25, px: 1, height: 20, minWidth: 0, lineHeight: 1, boxShadow: 'none', borderRadius: 1 }}>Start Assessment</Button>
                                : <Typography component="span" sx={{ fontSize: UI_TOKENS.text.labelXs, color: 'text.disabled' }}>None</Typography>
                            )}
                          </Box>
                        );
                      })()}

                    </Box>
                  );
                })()}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0, ml: 'auto' }}>
                  <Button
                    size="small"
                    variant="contained"
                    aria-label={riskApproved ? 'Continue setup' : 'Continue assessment'}
                    onClick={() => {
                      const assessment = appAssessments[app.id];
                      const needsAssessment = !assessment || ['Draft', 'Needs More Information', 'Clarification Needed'].includes(assessment.status);
                      if (needsAssessment && (isApplicationOwner(app) || isSuperAdmin)) {
                        handleOpenRiskAssessment(app);
                        return;
                      }
                      const pending = instances.find((instance) => !instance.ssoStatus || instance.ssoStatus === 'Not Started');
                      const canOnboardSso = canManageInstances(app) || canOnboardInstances(app) || isSSOAdmin || isSuperAdmin;
                      if (pending && canOnboardSso) handleOnboard(app.id, 'SSO', pending.id);
                      else if (instances[0]) handleViewSSO(app.id, instances[0].id);
                    }}
                    sx={{ fontSize: '0.72rem', fontWeight: 700, height: 26, px: 1.25, boxShadow: 'none' }}
                  >
                    {(appAssessments[app.id]?.status === 'Draft' || !appAssessments[app.id]) && (isApplicationOwner(app) || isSuperAdmin)
                      ? 'Continue assessment'
                      : 'Continue setup'}
                  </Button>
                  <IconButton
                    size="small"
                    aria-label="More application actions"
                    onClick={(event) => setCardMenu({ anchor: event.currentTarget, appId: app.id })}
                  >
                    <MoreVertIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })()}
          </Box>

          {/* Instances Section */}
          <Box sx={{ p: 1.5 }}>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'grey.200', overflowX: 'auto' }}>
              <Table size="small" sx={{
                minWidth: 860,
                ...listingTableSx,
                '@media (max-width: 767px)': {
                  minWidth: 0,
                  '& thead': { display: 'none' },
                  '& tbody tr': { display: 'block', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 },
                  '& tbody td': { display: 'block', borderBottom: 'none' },
                },
              }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    {[
                      { label: 'Date', width: 90 },
                      { label: 'Instance', width: 110 },
                      { label: 'Environment', width: 90 },
                      { label: 'SSO Type', width: 80 },
                      { label: 'Connection Name', width: 130 },
                      { label: 'Requested By', width: 110 },
                      { label: 'Assigned To', width: 110 },
                      { label: 'Secret Expires', width: 160 },
                      { label: 'Status', width: 110 },
                      { label: 'Actions', width: 120 },
                    ].map(({ label, width }) => (
                      <TableCell key={label} sx={{ width, minWidth: width, fontWeight: 700, fontSize: UI_TOKENS.text.labelXs, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', py: 1, whiteSpace: 'nowrap' }}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <caption style={{ captionSide: 'bottom', textAlign: 'left', padding: '8px 4px', fontSize: '0.72rem' }}>
                  Scroll sideways to see every column.
                </caption>
                <TableBody>
            {instances.map((instance) => {
              const ssoConfig = ssoConfigurations.find((c: any) =>
                c.instance_id === instance.id || (!c.instance_id && c.application_id === app.id)
              );
              const now = new Date();
              const expiryDate = ssoConfig?.client_secret_expires_at || ssoConfig?.certificate_expiration_date;
              const expiry = expiryDate ? new Date(expiryDate) : null;
              const daysToExpiry = expiry ? Math.ceil((expiry.getTime() - now.getTime()) / 86400000) : null;
              const statusKind =
                ['Approved', 'Completed', 'Active'].includes(instance.ssoStatus || '')
                  ? 'success'
                  : ['Pending Review', 'In Progress'].includes(instance.ssoStatus || '')
                    ? 'info'
                    : 'neutral';
              const ssoC = getStatusSurface(statusKind, resolvedMode);
              const environmentKind =
                instance.environment?.toLowerCase() === 'production'
                  ? 'success'
                  : instance.environment?.toLowerCase() === 'staging'
                    ? 'warning'
                    : 'neutral';
              const environmentSurface = getStatusSurface(environmentKind, resolvedMode);
              const protocolSurface = getProtocolSurface(ssoConfig?.sso_type || '', resolvedMode);
              const expiryKind =
                daysToExpiry !== null && daysToExpiry < 0
                  ? 'error'
                  : daysToExpiry !== null && daysToExpiry < 90
                    ? 'warning'
                    : 'success';
              const expirySurface = getStatusSurface(expiryKind, resolvedMode);

              return (
              <TableRow key={instance.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                {/* Date */}
                <TableCell sx={{ py: 1, whiteSpace: 'nowrap' }}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary' }}>
                    {ssoConfig?.created_at ? new Date(ssoConfig.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </Typography>
                </TableCell>

                {/* Instance */}
                <TableCell sx={{ py: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: UI_TOKENS.text.bodySm, color: 'text.primary' }}>{instance.name}</Typography>
                  {instance.description && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: UI_TOKENS.text.helper }}>{instance.description}</Typography>}
                </TableCell>

                {/* Environment */}
                <TableCell sx={{ py: 1 }}>
                  {instance.environment ? (
                    <Chip label={instance.environment} size="small" sx={{
                      height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600,
                      bgcolor: environmentSurface.bg,
                      color: environmentSurface.fg,
                      border: `1px solid ${environmentSurface.border}`,
                    }} />
                  ) : <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.disabled' }}>—</Typography>}
                </TableCell>

                {/* SSO Type */}
                <TableCell sx={{ py: 1 }}>
                  {ssoConfig?.sso_type ? (
                    <Chip label={ssoConfig.sso_type} size="small" sx={{
                      height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600,
                      bgcolor: protocolSurface.bg,
                      color: protocolSurface.fg,
                      border: `1px solid ${protocolSurface.border}`,
                    }} />
                  ) : <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.disabled' }}>—</Typography>}
                </TableCell>

                {/* Connection Name */}
                <TableCell sx={{ py: 1 }}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: ssoConfig?.connection_name ? 'text.primary' : 'text.disabled', fontStyle: ssoConfig?.connection_name ? 'normal' : 'italic' }} noWrap>
                    {ssoConfig?.connection_name || '—'}
                  </Typography>
                </TableCell>

                {/* Requested By */}
                <TableCell sx={{ py: 1 }}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }} noWrap>
                    {ssoConfig?.submitted_by_name || '—'}
                  </Typography>
                </TableCell>

                {/* Assigned To */}
                <TableCell sx={{ py: 1 }}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: ssoConfig?.assigned_to_name ? 'text.primary' : 'text.disabled', fontStyle: ssoConfig?.assigned_to_name ? 'normal' : 'italic' }} noWrap>
                    {ssoConfig?.assigned_to_name || 'Unassigned'}
                  </Typography>
                </TableCell>

                {/* Secret Expires */}
                <TableCell sx={{ py: 1 }}>
                  {daysToExpiry !== null || canRenewOidcSecret(app, ssoConfig) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      {daysToExpiry !== null ? (
                        <Chip
                          label={daysToExpiry < 0 ? 'Expired' : `${daysToExpiry}d`}
                          size="small"
                          sx={{
                            height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600,
                            bgcolor: expirySurface.bg,
                            color: expirySurface.fg,
                            border: `1px solid ${expirySurface.border}`,
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.disabled' }}>—</Typography>
                      )}
                      {canRenewOidcSecret(app, ssoConfig) && (
                        <RenewClientSecretControl
                          configId={ssoConfig.id}
                          applicationName={ssoConfig.application_name || app.name}
                          connectionName={ssoConfig.connection_name}
                          currentExpiresAt={ssoConfig.client_secret_expires_at}
                          onRenewed={(result) => handleRenewedSecret(ssoConfig.id, result)}
                          onError={(message) => setError(message)}
                        />
                      )}
                    </Box>
                  ) : <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.disabled' }}>—</Typography>}
                </TableCell>

                {/* Status */}
                <TableCell sx={{ py: 1 }}>
                  {instance.ssoStatus && instance.ssoStatus !== 'Not Started' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label={instance.ssoStatus} size="small" sx={{ height: 20, fontSize: UI_TOKENS.text.helper, fontWeight: 600, backgroundColor: ssoC.bg, color: ssoC.fg, border: `1px solid ${ssoC.border}`, '& .MuiChip-label': { px: 1 } }} />
                      {(canManageInstances(app) || canOnboardInstances(app) || isSSOAdmin) && (
                        <Tooltip title={instance.ssoStatus === 'Approved' ? 'View SSO Config (Read-only)' : 'View/Edit SSO Config'}>
                          <IconButton size="small" onClick={() => handleViewSSO(app.id, instance.id)} sx={{ p: 0.25 }}>
                            <VisibilityIcon sx={{ fontSize: '0.9rem', color: ssoC.fg }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ) : (canManageInstances(app) || canOnboardInstances(app) || isSSOAdmin) ? (
                    <Tooltip title="Onboard to SSO">
                      <Button size="small" variant="outlined" onClick={() => handleOnboard(app.id, 'SSO', instance.id)} sx={{ py: 0, px: 1, fontSize: UI_TOKENS.text.helper, minWidth: 0, height: 20, borderColor: 'grey.400', color: 'text.secondary', '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'primary.50' } }}>Onboard</Button>
                    </Tooltip>
                  ) : <Typography variant="caption" color="text.disabled">Not Started</Typography>}
                </TableCell>

                {/* Actions */}
                <TableCell sx={{ py: 1 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {canViewOidcCredentials(app, ssoConfig) && (
                      <Tooltip title="View OIDC credentials">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleShowOIDCCredentials(ssoConfig)}
                          sx={{ p: 0.5 }}
                        >
                          <InfoIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManageInstances(app) && (
                      <Tooltip title="Edit Instance">
                        <IconButton size="small" color="info" onClick={() => handleEditInstance(app, instance)} sx={{ p: 0.5 }}>
                          <EditIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View Notes & Comments">
                      <Badge invisible={!(unreadComments[instance.id] > 0)} badgeContent={unreadComments[instance.id] || 0} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 14, minWidth: 14 } }}>
                        <IconButton size="small" color="secondary" onClick={() => handleViewNotes(app.id, instance.id)} sx={{ p: 0.5 }}>
                          <TimelineIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                    {canManageInstances(app) && (
                      <Tooltip title="Delete Instance">
                        <IconButton size="small" color="error" onClick={() => handleDeleteInstance(app.id, instance.id)} sx={{ p: 0.5 }}>
                          <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
              );
            })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

    const renderPlatformTab = (platform: string) => {
    console.log('🔍 renderPlatformTab called with platform:', platform);
    const platformData = applications.flatMap(app => 
      getInstances(app).map(instance => ({
        ...instance,
        application: app
      }))
    );

    const getPlatformStatus = (instance: AppInstance) => {
      switch (platform.toLowerCase()) {
        case 'iga':
          return instance.igaStatus || 'Not Started';
        case 'sso':
          return instance.ssoStatus || 'Not Started';
        case 'pam':
          return instance.pamStatus || 'Not Started';
        default:
          return 'Not Started';
      }
    };

    // Filter platform data using main component state
    const getFilteredPlatformData = () => {
      return platformData.filter(({ application, ...instance }) => {
        // Search term filter
        const searchLower = platformSearchTerm.toLowerCase();
        const matchesSearch = platformSearchTerm === '' || 
          application.name.toLowerCase().includes(searchLower) ||
          (application.description && application.description.toLowerCase().includes(searchLower)) ||
          (application.business_function && application.business_function.toLowerCase().includes(searchLower)) ||
          instance.name.toLowerCase().includes(searchLower);

        // Environment filter
        const matchesEnvironment = platformEnvironmentFilter === 'all' || 
          instance.environment === platformEnvironmentFilter;

        // Platform status filter
        const matchesPlatformStatus = platformTabStatusFilter === 'all' || 
          getPlatformStatus(instance) === platformTabStatusFilter;

        // Risk filter
        const matchesRisk = platformRiskFilter === 'all' || 
          application.risk_level === platformRiskFilter;

        return matchesSearch && matchesEnvironment && matchesPlatformStatus && matchesRisk;
      });
    };

    // Pagination logic for applications
    const filteredData = sortListing(getFilteredPlatformData(), platformListingSort, {
      getName: (row: any) => row.application?.name || row.name,
      getDate: (row: any) => row.created_at || row.application?.created_at,
    });
    const totalItems = filteredData.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" gutterBottom>
            {platform.toUpperCase()} Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={platformViewMode}
              exclusive
              onChange={(e, newMode) => newMode && setPlatformViewMode(newMode)}
              size="small"
              sx={{ mr: 2 }}
            >
              <ToggleButton value="table" aria-label="table view">
                <TableViewIcon />
              </ToggleButton>
              <ToggleButton value="card" aria-label="card view">
                <CardViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
            >
              Refresh
            </Button>
          <Button
            variant="contained"
            onClick={() => setActiveTab('all')}
          >
            Back to Applications
          </Button>
          </Box>
        </Box>
        
        {/* Information about the platform tab */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            This tab shows all applications and their {platform.toUpperCase()} onboarding status. 
            Use the "View App Details" button to see the full application information in the main Applications view.
          </Typography>
        </Alert>

        {/* SSO Status Overview Tiles - Only for SSO tab */}
        {platform.toLowerCase() === 'sso' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              SSO pipeline: {ssoStatistics?.pipeline?.completed ?? (getStatusCounts()['Approved'] + getStatusCounts()['Completed'])} onboarded
              {' · '}
              {ssoStatistics?.pipeline?.pending ?? (getStatusCounts()['Pending Review'] + getStatusCounts()['In Progress'] + getStatusCounts()['Need More Info'])} pending
              {' · '}
              {ssoStatistics?.pipeline?.total ?? (
                getStatusCounts()['Approved'] +
                getStatusCounts()['Completed'] +
                getStatusCounts()['Pending Review'] +
                getStatusCounts()['In Progress'] +
                getStatusCounts()['Need More Info']
              )} together
            </Typography>
          </Box>
        )}
        {platform.toLowerCase() === 'sso' && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ textAlign: 'center' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Badge max={999} badgeContent={ssoStatistics?.pipeline?.byStatus?.['Pending Review'] ?? getStatusCounts()['Pending Review']} color="warning">
                    <Schedule sx={{ fontSize: 24, color: '#F59E0B' }} />
                  </Badge>
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                    Pending Review
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ textAlign: 'center' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Badge max={999} badgeContent={ssoStatistics?.pipeline?.byStatus?.['In Progress'] ?? getStatusCounts()['In Progress']} color="info">
                    <PlayArrow sx={{ fontSize: 24, color: '#0F766E' }} />
                  </Badge>
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                    In Progress
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ textAlign: 'center' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Badge max={999} badgeContent={ssoStatistics?.pipeline?.byStatus?.['Approved'] ?? getStatusCounts()['Approved']} color="primary">
                    <CheckCircleOutline sx={{ fontSize: 24, color: '#3B82F6' }} />
                  </Badge>
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                    Approved
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ textAlign: 'center' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Badge max={999} badgeContent={ssoStatistics?.pipeline?.byStatus?.['Completed'] ?? getStatusCounts()['Completed']} color="success">
                    <CheckCircleOutline sx={{ fontSize: 24, color: '#059669' }} />
                  </Badge>
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                    Completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ textAlign: 'center' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Badge max={999} badgeContent={ssoStatistics?.pipeline?.byStatus?.['Rejected'] ?? getStatusCounts()['Rejected']} color="error">
                    <Warning sx={{ fontSize: 24, color: '#DC2626' }} />
                  </Badge>
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                    Rejected
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ textAlign: 'center' }}>
                <CardContent sx={{ p: 1.5 }}>
                  <Badge max={999} badgeContent={ssoStatistics?.pipeline?.byStatus?.['Need More Info'] ?? getStatusCounts()['Need More Info']} color="secondary">
                    <HelpOutline sx={{ fontSize: 24, color: '#8B5CF6' }} />
                  </Badge>
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                    Need More Info
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}



        {/* Platform-specific Filters */}
        {/* Search and Filters Section - Compact Layout */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              {/* Search Bar */}
              <TextField
                placeholder="Search applications..."
                value={platformSearchTerm}
                onChange={(e) => setPlatformSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 250, flexGrow: 1 }}
                size="small"
              />
              
              {/* Filter Dropdowns */}
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={platformTabStatusFilter}
                  onChange={(e) => setPlatformTabStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Pending Review">Pending Review</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                  <MenuItem value="Need More Info">Need More Info</MenuItem>
                </Select>
              </FormControl>

              <ListingSortSelect
                value={platformListingSort}
                onChange={(value) => { setPlatformListingSort(value); setCurrentPage(1); }}
              />
              
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>SSO Type</InputLabel>
                <Select
                  value="all"
                  label="SSO Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="SAML">SAML</MenuItem>
                  <MenuItem value="OIDC">OIDC</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Assignee</InputLabel>
                <Select
                  value="all"
                  label="Assignee"
                >
                  <MenuItem value="all">All Assignees</MenuItem>
                  <MenuItem value="unassigned">Unassigned</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel>Instance</InputLabel>
                <Select
                  value="all"
                  label="Instance"
                >
                  <MenuItem value="all">All Instances</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {platformViewMode === 'table' ? (
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table size="small" sx={{ ...listingTableSx, '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.5)' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Application</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Instance</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Environment</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Owners</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>{platform.toUpperCase()} Status</TableCell>
                  {platform.toLowerCase() === 'sso' && (
                    <>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>SSO Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Assigned To</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Secret Expires</TableCell>
                    </>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map(({ application, ...instance }) => (
                <TableRow key={instance.id} hover>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {(() => {
                        const config = ssoConfigurations.find(c => c.instance_id === instance.id);
                        return config ? new Date(config.created_at).toLocaleDateString() : '-';
                      })()}
                      </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.875rem' }}>
                      {application.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      {instance.name}
                          </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Chip label={instance.environment} size="small" sx={{ fontSize: UI_TOKENS.text.caption, height: 24 }} />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    {/* Only show owner management for non-SSO admins */}
                    {!isSSOAdmin && (
                      <Tooltip title="Click to view all owners">
                        <IconButton
                          size="small"
                          onClick={() => handleShowOwners(application)}
                          sx={{ color: 'primary.main' }}
                        >
                          <AccountCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isSSOAdmin && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Owner management restricted
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Chip
                      icon={getStatusIcon(getPlatformStatus(instance))}
                      label={getPlatformStatus(instance)}
                      color={getOnboardingStatusColor(getPlatformStatus(instance))}
                      size="small"
                      sx={{ fontSize: UI_TOKENS.text.caption, height: 24 }}
                    />
                  </TableCell>
                  {(() => {
                    console.log('🔍 Checking SSO columns condition:', {
                      platform: platform,
                      platformLower: platform.toLowerCase(),
                      isSSO: platform.toLowerCase() === 'sso',
                      instanceId: instance.id,
                      instanceName: instance.name
                    });
                    return platform.toLowerCase() === 'sso';
                  })() && (
                    <>
                      <TableCell sx={{ py: 1 }}>
                        <Chip 
                          label={instance.ssoType || '-'} 
                          size="small" 
                          sx={{ fontSize: UI_TOKENS.text.caption, height: 24 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {instance.assignedTo || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        {(() => {
                          const config = ssoConfigurations.find(c => c.instance_id === instance.id);
                          const canRenew = canRenewOidcSecret(application, config);
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', minWidth: 88 }}>
                                {formatSecretExpiry(config?.client_secret_expires_at)}
                              </Typography>
                              {canRenew && config && (
                                <RenewClientSecretControl
                                  configId={config.id}
                                  applicationName={config.application_name || application.name}
                                  connectionName={config.connection_name}
                                  currentExpiresAt={config.client_secret_expires_at}
                                  onRenewed={(result) => handleRenewedSecret(config.id, result)}
                                  onError={(message) => setError(message)}
                                />
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                    </>
                  )}
                  <TableCell align="right" sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                      {canPlatformOnboard(application, platform) && (
                        <>
                          {/* IGA Onboarding - only if not already onboarded */}
                          {platform.toLowerCase() === 'iga' && (!instance.igaStatus || instance.igaStatus === 'Not Started' || instance.igaStatus === 'Rejected') && (
                            <Tooltip title="Onboard to IGA">
                              <Button
                                size="small"
                                onClick={() => handleOnboard(application.id, 'IGA')}
                                variant="outlined"
                                color="primary"
                                startIcon={<AccountCircleIcon />}
                              >
                                Onboard
                              </Button>
                            </Tooltip>
                          )}
                          
                          {/* SSO - Show different buttons based on status */}
                          {platform.toLowerCase() === 'sso' && (
                            instance.ssoStatus === 'Not Started' ? (
                              <Tooltip title="Onboard to SSO">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOnboard(application.id, 'SSO', instance.id)}
                                  sx={{ 
                                    color: 'primary.main',
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.50' }
                                  }}
                                >
                                  <SecurityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : instance.ssoStatus === 'Approved' ? (
                              <Tooltip title="View SSO Configuration (Read-only)">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewSSO(application.id, instance.id)}
                                  sx={{ 
                                    color: 'success.main',
                                    border: '1px solid',
                                    borderColor: 'success.main',
                                    '&:hover': { backgroundColor: 'success.50' }
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="View/Edit SSO Configuration">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewSSO(application.id, instance.id)}
                                  sx={{ 
                                    color: 'info.main',
                                    border: '1px solid',
                                    borderColor: 'info.main',
                                    '&:hover': { backgroundColor: 'info.50' }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                          
                          {/* PAM Onboarding - only if not already onboarded */}
                          {platform.toLowerCase() === 'pam' && (!instance.pamStatus || instance.pamStatus === 'Not Started' || instance.pamStatus === 'Rejected') && (
                            <Tooltip title="Onboard to PAM">
                              <Button
                                size="small"
                                onClick={() => handleOnboard(application.id, 'PAM')}
                                variant="outlined"
                                color="warning"
                                startIcon={<VpnKeyIcon />}
                              >
                                Onboard
                              </Button>
                            </Tooltip>
                          )}
                        </>
                      )}
                      
                      {/* SSO Details (i) icon - Show for all users when there's an SSO configuration */}
                      {platform.toLowerCase() === 'sso' && (() => {
                        const config = ssoConfigurations.find(c => c.instance_id === instance.id);
                        if (!config) return null;
                        return (
                          <>
                            {canViewOidcCredentials(application, config) && (
                              <Tooltip title="View OIDC credentials">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleShowOIDCCredentials(config)}
                                  sx={{ color: 'secondary.main' }}
                                >
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="View SSO Details & Comments">
                              <IconButton
                                size="small"
                                onClick={() => handleShowSSODetails(application)}
                                sx={{ color: 'primary.main' }}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        );
                      })()}

                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        ) : (
          <Grid container spacing={3}>
            {paginatedData.map(({ application, ...instance }) => (
              <Grid item xs={12} md={6} lg={4} key={instance.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {application.name}
                      </Typography>
                      <Chip
                        icon={getStatusIcon(getPlatformStatus(instance))}
                        label={getPlatformStatus(instance)}
                        color={getOnboardingStatusColor(getPlatformStatus(instance))}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Instance:</strong> {instance.name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Environment:</strong> {instance.environment}
                    </Typography>
                    
                    {platform.toLowerCase() === 'sso' && (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          <strong>SSO Type:</strong> {instance.ssoType || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          <strong>Assigned To:</strong> {instance.assignedTo || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Secret Expires:</strong>{' '}
                            {(() => {
                              const config = ssoConfigurations.find(c => c.instance_id === instance.id);
                              return formatSecretExpiry(config?.client_secret_expires_at);
                            })()}
                          </Typography>
                          {(() => {
                            const config = ssoConfigurations.find(c => c.instance_id === instance.id);
                            return canRenewOidcSecret(application, config) && config ? (
                              <RenewClientSecretControl
                                configId={config.id}
                                applicationName={config.application_name || application.name}
                                connectionName={config.connection_name}
                                currentExpiresAt={config.client_secret_expires_at}
                                onRenewed={(result) => handleRenewedSecret(config.id, result)}
                                onError={(message) => setError(message)}
                              />
                            ) : null;
                          })()}
                        </Box>
                        {(() => {
                          const config = ssoConfigurations.find(c => c.instance_id === instance.id);
                          return canViewOidcCredentials(application, config) && config ? (
                            <Box sx={{ mb: 2 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<InfoIcon />}
                                onClick={() => handleShowOIDCCredentials(config)}
                              >
                                View credentials
                              </Button>
                            </Box>
                          ) : null;
                        })()}
                      </>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Date:</strong> {(() => {
                        const config = ssoConfigurations.find(c => c.instance_id === instance.id);
                        return config ? new Date(config.created_at).toLocaleDateString() : '-';
                      })()}
                    </Typography>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {canPlatformOnboard(application, platform) && (
                        <>
                          {/* IGA Onboarding - only if not already onboarded */}
                          {platform.toLowerCase() === 'iga' && (!instance.igaStatus || instance.igaStatus === 'Not Started' || instance.igaStatus === 'Rejected') && (
                            <Button
                              size="small"
                              onClick={() => handleOnboard(application.id, 'IGA')}
                              variant="outlined"
                              color="primary"
                              startIcon={<AccountCircleIcon />}
                            >
                              Onboard
                            </Button>
                          )}
                          
                          {/* SSO - Show different buttons based on status */}
                          {platform.toLowerCase() === 'sso' && (
                            instance.ssoStatus === 'Not Started' ? (
                              <Button
                                size="small"
                                onClick={() => handleOnboard(application.id, 'SSO', instance.id)}
                                variant="outlined"
                                color="primary"
                                startIcon={<SecurityIcon />}
                              >
                                Onboard
                              </Button>
                            ) : instance.ssoStatus === 'Approved' ? (
                              <Button
                                size="small"
                                onClick={() => handleViewSSO(application.id, instance.id)}
                                variant="outlined"
                                color="info"
                                startIcon={<VisibilityIcon />}
                              >
                                View
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                onClick={() => handleViewSSO(application.id, instance.id)}
                                variant="outlined"
                                color="secondary"
                                startIcon={<VisibilityIcon />}
                              >
                                View
                              </Button>
                            )
                          )}
                          
                          {/* PAM Onboarding - only if not already onboarded */}
                          {platform.toLowerCase() === 'pam' && (!instance.pamStatus || instance.pamStatus === 'Not Started' || instance.pamStatus === 'Rejected') && (
                            <Button
                              size="small"
                              onClick={() => handleOnboard(application.id, 'PAM')}
                              variant="outlined"
                              color="primary"
                              startIcon={<SecurityIcon />}
                            >
                              Onboard
                            </Button>
                          )}
                        </>
                      )}
                      
                      <Button
                        size="small"
                        onClick={() => setActiveTab('all')}
                        variant="outlined"
                        color="secondary"
                        startIcon={<VisibilityIcon />}
                      >
                        View App
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
        
        <ListingPagination
          page={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      </Box>
    );
  };

  if (loading) {
    return <CenteredLoader />;
  }

  return (
    <Box sx={{ p: currentOnboardingType === 'sso' ? 0 : 3 }}>
      {/* Notification System */}
      {showNotification && notifications.length > 0 && (
        <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
          {notifications.map((notification) => (
            <Alert
              key={notification.id}
              severity={notification.type}
              sx={{ mb: 1, minWidth: 300 }}
              onClose={() => {
                setShowNotification(false);
                setNotifications(prev => prev.filter(n => n.id !== notification.id));
              }}
            >
              {notification.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Submenu Navigation for SSO Admins and Super Admins */}

      {/* Main Header */}
      {currentOnboardingType !== 'sso' && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        {/* Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AppsIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>
              {currentOnboardingType === 'iga' ? 'IGA Onboarding' :
               currentOnboardingType === 'pam' ? 'PAM Onboarding' :
               'Applications Management'}
            </Typography>
            <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary' }}>
              {applications.length} application{applications.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {isAdmin && (
            <Button size="small" variant="outlined" startIcon={<DownloadIcon sx={{ fontSize: '0.9rem !important' }} />}
              onClick={exportToCSV} disabled={applications.length === 0} sx={{ fontSize: UI_TOKENS.text.buttonSm, py: UI_TOKENS.menu.itemPaddingY }}>
              Export CSV
            </Button>
          )}

          {isSuperAdmin && (
            <>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: '0.9rem !important' }} />}
                endIcon={<ArrowDownIcon sx={{ fontSize: '0.9rem !important' }} />}
                onClick={(e) => {
                  void refreshConfiguredImportIdps();
                  setAddMenuAnchor(e.currentTarget);
                }}
                sx={{ fontSize: UI_TOKENS.text.buttonSm, py: UI_TOKENS.menu.itemPaddingY }}
              >
                Add Application
              </Button>
              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { mt: 0.5, minWidth: 200, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
              >
                <MenuItem
                  onClick={() => { setAddMenuAnchor(null); handleOpenDialog(); }}
                  sx={{ fontSize: UI_TOKENS.text.bodySm, py: 1 }}
                >
                  <ListItemIcon><FormIcon sx={{ fontSize: '1rem', color: 'primary.main' }} /></ListItemIcon>
                  <ListItemText primary="Use Form" primaryTypographyProps={{ fontSize: UI_TOKENS.text.bodySm }} />
                </MenuItem>
                <MenuItem
                  onClick={() => { setAddMenuAnchor(null); setImportDialogOpen(true); }}
                  sx={{ fontSize: UI_TOKENS.text.bodySm, py: 1 }}
                >
                  <ListItemIcon><UploadIcon sx={{ fontSize: '1rem', color: 'success.main' }} /></ListItemIcon>
                  <ListItemText primary="Import from CSV" primaryTypographyProps={{ fontSize: UI_TOKENS.text.bodySm }} />
                </MenuItem>
                {configuredImportIdps.length > 0 && (
                <MenuItem
                  onClick={() => { setAddMenuAnchor(null); void openImportIdpDialog(); }}
                  sx={{ fontSize: UI_TOKENS.text.bodySm, py: 1 }}
                >
                  <ListItemIcon><CloudUploadIcon sx={{ fontSize: '1rem', color: 'warning.main' }} /></ListItemIcon>
                  <ListItemText primary="Import from IdP" primaryTypographyProps={{ fontSize: UI_TOKENS.text.bodySm }} />
                </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      </Box>
      )}



      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}



      {/* SSO Onboarding Tab Content */}
      {((isSSOAdmin || isSuperAdmin) || (ssoConfigurations.length > 0)) && activeTab === 'sso' && (
        <Box>
          {renderPlatformTab('sso')}
        </Box>
      )}

      {/* Application Dashboard - Only for SSO Admins and not on SSO onboarding page */}
      {isSSOAdmin && currentOnboardingType !== 'sso' && activeTab !== 'sso' && (
        <Box sx={{ mb: 3 }}>
          {/* Summary Cards Row */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ 
                p: 1.5, 
                textAlign: 'center',
                borderRadius: 1.5,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#666',
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  mb: 0.5
                }}>
                  TOTAL APPS
                </Typography>
                <Typography variant="h5" sx={{ 
                  color: 'text.primary',
                  fontWeight: '700',
                  fontSize: '1.8rem',
                  mb: 0.25
                }}>
                  {stats.totalApps}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#999',
                  fontSize: UI_TOKENS.text.helper
                }}>
                  Applications in system
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ 
                p: 1.5, 
                textAlign: 'center',
                borderRadius: 1.5,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#666',
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  mb: 0.5
                }}>
                  TOTAL INSTANCES
                </Typography>
                <Typography variant="h5" sx={{ 
                  color: 'text.primary',
                  fontWeight: '700',
                  fontSize: '1.8rem',
                  mb: 0.25
                }}>
                  {stats.totalInstances}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#999',
                  fontSize: UI_TOKENS.text.helper
                }}>
                  Application instances
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ 
                p: 1.5, 
                textAlign: 'center',
                borderRadius: 1.5,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#666',
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  mb: 0.5
                }}>
                  SSO APPS
                </Typography>
                <Typography variant="h5" sx={{ 
                  color: '#4caf50',
                  fontWeight: '700',
                  fontSize: '1.8rem',
                  mb: 0.25
                }}>
                  {stats.appsWithSSO}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#999',
                  fontSize: UI_TOKENS.text.helper
                }}>
                  Applications with SSO configured
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ 
                p: 1.5, 
                textAlign: 'center',
                borderRadius: 1.5,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="body2" sx={{ 
                  color: '#666',
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  mb: 0.5
                }}>
                  SSO INSTANCES
                </Typography>
                <Typography variant="h5" sx={{ 
                  color: '#4caf50',
                  fontWeight: '700',
                  fontSize: '1.8rem',
                  mb: 0.25
                }}>
                  {stats.instancesWithSSO}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#999',
                  fontSize: UI_TOKENS.text.helper
                }}>
                  Instances with SSO configured
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Progress Cards Row */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <Card sx={{ 
                p: 2,
                borderRadius: 1.5,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    backgroundColor: '#4caf50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5
                  }}>
                    <Typography sx={{ color: 'white', fontSize: '1rem' }}>🔑</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: '600',
                    color: 'text.primary',
                    fontSize: '0.95rem'
                  }}>
                    SSO Applications Progress
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ 
                  color: '#666',
                  mb: 1.5,
                  fontSize: '0.8rem'
                }}>
                  {stats.appsWithSSO} of {stats.totalApps} Applications
                </Typography>
                <Box sx={{ 
                  width: '100%', 
                  height: 6, 
                  backgroundColor: '#e0e0e0', 
                  borderRadius: 3,
                  mb: 1,
                  overflow: 'hidden'
                }}>
                  <Box sx={{ 
                    width: `${stats.appOnboardingPercentage}%`, 
                    height: '100%', 
                    backgroundColor: '#4caf50',
                    transition: 'width 0.3s ease'
                  }} />
                </Box>
                <Typography variant="h6" sx={{ 
                  color: stats.appOnboardingPercentage === 100 ? '#4caf50' : 'text.primary',
                  fontWeight: '700',
                  fontSize: '1.4rem'
                }}>
                  {stats.appOnboardingPercentage.toFixed(1)}%
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ 
                p: 2,
                borderRadius: 1.5,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '50%', 
                    backgroundColor: '#4caf50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5
                  }}>
                    <Typography sx={{ color: 'white', fontSize: '1rem' }}>⚙️</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ 
                    fontWeight: '600',
                    color: 'text.primary',
                    fontSize: '0.95rem'
                  }}>
                    SSO Instances Progress
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ 
                  color: '#666',
                  mb: 1.5,
                  fontSize: '0.8rem'
                }}>
                  {stats.instancesWithSSO} of {stats.totalInstances} instances
                </Typography>
                <Box sx={{ 
                  width: '100%', 
                  height: 6, 
                  backgroundColor: '#e0e0e0', 
                  borderRadius: 3,
                  mb: 1,
                  overflow: 'hidden'
                }}>
                  <Box sx={{ 
                    width: `${stats.instanceOnboardingPercentage}%`, 
                    height: '100%', 
                    backgroundColor: '#4caf50',
                    transition: 'width 0.3s ease'
                  }} />
                </Box>
                <Typography variant="h6" sx={{ 
                  color: stats.instanceOnboardingPercentage === 100 ? '#4caf50' : 'text.primary',
                  fontWeight: '700',
                  fontSize: '1.4rem'
                }}>
                  {stats.instanceOnboardingPercentage.toFixed(1)}%
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tab Content */}
      {activeTab === 'all' && (
        <Box>
          {/* Filters Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600, mb: 1 }}>
                Filters & Search
              </Typography>
              {(() => {
                const INPUT_SX = {
                  '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
                  '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
                  '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
                };
                const MENU_PROPS = {
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        fontSize: UI_TOKENS.text.bodySm,
                        minHeight: UI_TOKENS.menu.itemMinHeight,
                        py: UI_TOKENS.menu.itemPaddingY,
                      },
                    },
                  },
                };
                return (
                  <Grid container spacing={1.5}>
                    {/* Search */}
                    <Grid item xs={12} md={3}>
                      <Button
                        size="small"
                        variant={showDemoRows ? 'contained' : 'outlined'}
                        sx={{ textTransform: 'none', mb: 1 }}
                        onClick={() => {
                          const next = !showDemoRows;
                          setShowDemoRows(next);
                          try {
                            localStorage.setItem('provysn-show-demo-rows', next ? '1' : '0');
                          } catch {
                            /* ignore */
                          }
                        }}
                      >
                        {showDemoRows ? 'Hide tagged demo rows' : 'Show tagged demo rows'}
                      </Button>
                      <TextField
                        fullWidth
                        label="Search Applications"
                        placeholder="Search by name, description, owners, or delegates"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        sx={INPUT_SX}
                      />
                    </Grid>

                    {/* Status Filter */}
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" sx={INPUT_SX}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          label="Status"
                          MenuProps={MENU_PROPS}
                        >
                          <MenuItem value="all">All Status</MenuItem>
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Inactive">Inactive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Risk Filter */}
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" sx={INPUT_SX}>
                        <InputLabel>Risk Level</InputLabel>
                        <Select
                          value={riskFilter}
                          onChange={(e) => setRiskFilter(e.target.value)}
                          label="Risk Level"
                          MenuProps={MENU_PROPS}
                        >
                          <MenuItem value="all">All Risk Levels</MenuItem>
                          <MenuItem value="Low">Low</MenuItem>
                          <MenuItem value="Medium">Medium</MenuItem>
                          <MenuItem value="High">High</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Environment Filter */}
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" sx={INPUT_SX}>
                        <InputLabel>Environment</InputLabel>
                        <Select
                          value={environmentFilter}
                          onChange={(e) => setEnvironmentFilter(e.target.value)}
                          label="Environment"
                          MenuProps={MENU_PROPS}
                        >
                          <MenuItem value="all">All Environments</MenuItem>
                          <MenuItem value="Production">Production</MenuItem>
                          <MenuItem value="Staging">Staging</MenuItem>
                          <MenuItem value="Development">Development</MenuItem>
                          <MenuItem value="UAT">UAT</MenuItem>
                          <MenuItem value="Testing">Testing</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Platform Status Filter */}
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small" sx={INPUT_SX}>
                        <InputLabel>Platform Status</InputLabel>
                        <Select
                          value={platformStatusFilter}
                          onChange={(e) => setPlatformStatusFilter(e.target.value)}
                          label="Platform Status"
                          MenuProps={MENU_PROPS}
                        >
                          <MenuItem value="all">All Statuses</MenuItem>
                          <MenuItem value="Not Started">Not Started</MenuItem>
                          <MenuItem value="Pending Review">Pending Review</MenuItem>
                          <MenuItem value="Need More Info">Need More Info</MenuItem>
                          <MenuItem value="In Progress">In Progress</MenuItem>
                          <MenuItem value="Approved">Approved</MenuItem>
                          <MenuItem value="Completed">Completed</MenuItem>
                          <MenuItem value="Rejected">Rejected</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <ListingSortSelect
                        value={listingSort}
                        onChange={setListingSort}
                        sx={INPUT_SX}
                        menuProps={MENU_PROPS}
                      />
                    </Grid>

                    {/* Clear Filters Button */}
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('all');
                          setRiskFilter('all');
                          setEnvironmentFilter('all');
                          setPlatformStatusFilter('all');
                        }}
                        sx={{ fontSize: UI_TOKENS.text.buttonSm, mt: 0.5 }}
                      >
                        Clear All Filters
                      </Button>
                    </Grid>
                  </Grid>
                );
              })()}
            </CardContent>
          </Card>

          {/* Applications List */}
          {(() => {
            // Pagination logic for main applications
            const filteredApplications = sortListing(getFilteredApplications(), listingSort);
            const totalItems = filteredApplications.length;
            const totalPages = Math.ceil(totalItems / mainItemsPerPage);
            const startIndex = (mainCurrentPage - 1) * mainItemsPerPage;
            const endIndex = startIndex + mainItemsPerPage;
            const paginatedApplications = filteredApplications.slice(startIndex, endIndex);

            return (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm }} color="text.secondary">
                    {totalItems} instance{totalItems !== 1 ? 's' : ''}
                    {searchTerm || statusFilter !== 'all' || riskFilter !== 'all' || environmentFilter !== 'all' || platformStatusFilter !== 'all' ? ' (filtered)' : ''}
                  </Typography>
                </Box>
                
                {filteredApplications.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      {applications.length === 0 ? 'No applications found' : 'No applications match the current filters'}
                    </Typography>
                    {applications.length === 0 && isSuperAdmin && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        sx={{ mt: 2 }}
                      >
                        Add Your First Application
                      </Button>
                    )}
                  </Box>
                ) : (
                  <>
                    {paginatedApplications.map((app, idx) => renderApplicationCard(app, idx))}
                    
                    <ListingPagination
                      page={mainCurrentPage}
                      totalPages={totalPages}
                      totalItems={totalItems}
                      pageSize={mainItemsPerPage}
                      onPageChange={setMainCurrentPage}
                      onPageSizeChange={(value) => {
                        setMainItemsPerPage(value);
                        setMainCurrentPage(1);
                      }}
                      pageSizeSelectSx={{
                        '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
                        '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
                        '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
                      }}
                      pageSizeMenuProps={{
                        PaperProps: {
                          sx: {
                            '& .MuiMenuItem-root': {
                              fontSize: UI_TOKENS.text.bodySm,
                              minHeight: UI_TOKENS.menu.itemMinHeight,
                              py: UI_TOKENS.menu.itemPaddingY,
                            },
                          },
                        },
                      }}
                    />
                  </>
                )}
              </>
            );
          })()}
        </Box>
      )}

      {activeTab === 'iga' && renderPlatformTab('iga')}
      {activeTab === 'pam' && renderPlatformTab('pam')}

      <Menu
        anchorEl={cardMenu?.anchor || null}
        open={Boolean(cardMenu)}
        onClose={() => setCardMenu(null)}
      >
        {(() => {
          const menuApp = applications.find((item) => item.id === cardMenu?.appId);
          if (!menuApp) return null;
          return (
            <>
              {(canManageInstances(menuApp) || isSuperAdmin) && (
                <MenuItem onClick={() => { setCardMenu(null); handleOpenInstanceDialog(menuApp); }}>Add instance</MenuItem>
              )}
              {canManageApplication(menuApp) && (
                <MenuItem onClick={() => { setCardMenu(null); handleOpenDelegatesDialog(menuApp); }}>Manage delegates</MenuItem>
              )}
              {isSuperAdmin && (
                <MenuItem onClick={() => { setCardMenu(null); handleEditApplication(menuApp); }}>Edit application</MenuItem>
              )}
              {isSuperAdmin && (
                <MenuItem onClick={() => { setCardMenu(null); handleDeleteApplication(menuApp); }}>Delete application</MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      {/* Application dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          window.setTimeout(handleCloseDialog, 0);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AppsIcon sx={{ color: 'primary.main', fontSize: 18 }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
              {editingApplication ? 'Edit Application' : 'Add New Application'}
            </Typography>
          </Box>
          <IconButton size="small" aria-label="Close" onClick={handleCloseDialog}><CancelIcon sx={{ fontSize: '1rem' }} /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {!editingApplication && (
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary', mb: 1.5 }}>
              {`Step ${setupStep + 1} of 3. ${setupStep === 0 ? 'Choose a catalog starter or a custom application.' : setupStep === 1 ? 'Confirm the name, protocol, and environment.' : 'Confirm the owners. Nothing is created in the identity provider yet.'}`}
            </Typography>
          )}
          {!editingApplication && setupStep === 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary', mb: 1 }}>
                Start from a known shape, then confirm the name, owners, and environment. This does not create the identity-provider application.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {[
                  { name: 'Slack', note: 'SAML. You will need the ACS URL and a certificate before approval.' },
                  { name: 'GitLab', note: 'SAML. Confirm the entity ID and ACS URL for the chosen environment.' },
                  { name: 'Microsoft Entra', note: 'OIDC. Approval can create an Entra app registration after you choose a decision.' },
                  { name: 'Okta', note: 'OIDC. Approval can create an Okta application after you choose a decision.' },
                ].map((starter) => (
                  <Button
                    key={starter.name}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const protocol = /OIDC/.test(starter.note) ? 'OIDC' : 'SAML';
                      setSetupProtocol(protocol);
                      setFormData((current) => ({
                        ...current,
                        name: starter.name,
                        description: starter.note,
                      }));
                      setSetupStep(1);
                    }}
                  >
                    {starter.name}
                  </Button>
                ))}
              </Box>
              <Button size="small" sx={{ mt: 1.5 }} onClick={() => setSetupStep(1)}>Custom application</Button>
            </Box>
          )}
          <Grid container spacing={1.5} sx={{ display: editingApplication || setupStep > 0 ? 'flex' : 'none' }}>
            <Grid item xs={12} md={6}>
              <TextField size="small" fullWidth label="Application Name" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })} required
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField size="small" fullWidth label="Application ID" value={formData.application_id}
                onChange={(e) => setFormData({ ...formData, application_id: e.target.value })} required
                disabled={!editingApplication}
                helperText={editingApplication ? undefined : 'Generated for this application. You do not need to type one.'}
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" fullWidth label="Description" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline rows={2}
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField size="small" fullWidth label="Business Function" value={formData.business_function}
                onChange={(e) => setFormData({ ...formData, business_function: e.target.value })} required
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm } }}>
                <InputLabel>Risk Level</InputLabel>
                <Select value={formData.risk_level} onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })} label="Risk Level"
                  MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: UI_TOKENS.text.bodySm, minHeight: UI_TOKENS.menu.itemMinHeight, py: UI_TOKENS.menu.itemPaddingY } } } }}>
                  <MenuItem value="Not Defined">Not Defined</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Medium">Medium</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Critical">Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm } }}>
                <InputLabel>Status</InputLabel>
                <Select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} label="Status"
                  MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: UI_TOKENS.text.bodySm, minHeight: UI_TOKENS.menu.itemMinHeight, py: UI_TOKENS.menu.itemPaddingY } } } }}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {!editingApplication && (setupStep === 1 || setupStep === 2) && (
              <>
                <Grid item xs={12} md={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Protocol</InputLabel>
                    <Select value={setupProtocol} label="Protocol" onChange={(e) => setSetupProtocol(e.target.value)}>
                      <MenuItem value="SAML">SAML</MenuItem>
                      <MenuItem value="OIDC">OIDC</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Environment</InputLabel>
                    <Select value={setupEnvironment} label="Environment" onChange={(e) => setSetupEnvironment(e.target.value)}>
                      <MenuItem value="Development">Development</MenuItem>
                      <MenuItem value="Staging">Staging</MenuItem>
                      <MenuItem value="Production">Production</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Owner Management */}
            {(editingApplication || setupStep === 2) && isSuperAdmin && !isSSOAdmin && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <PeopleIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                    <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      Owner Management
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    size="small"
                    multiple
                    options={users}
                    getOptionLabel={(option: User) => `${option.first_name} ${option.last_name} (${option.email})`}
                    value={users.filter((user: User) => (formData.primary_owner_ids || []).includes(user.id))}
                    onChange={(event: any, newValue: User[]) => setFormData({ ...formData, primary_owner_ids: newValue.map((u: User) => u.id) })}
                    renderInput={(params: any) => (
                      <TextField {...params} label="Primary Owners" placeholder="Search users…" required
                        sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }} />
                    )}
                    renderOption={(props: any, option: User) => (
                      <li {...props}>
                        <Box>
                          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>{option.first_name} {option.last_name}</Typography>
                          <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, color: 'text.secondary' }}>{option.email}</Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    size="small"
                    multiple
                    options={users}
                    getOptionLabel={(option: User) => `${option.first_name} ${option.last_name} (${option.email})`}
                    value={users.filter((user: User) => (formData.secondary_owner_ids || []).includes(user.id))}
                    onChange={(event: any, newValue: User[]) => setFormData({ ...formData, secondary_owner_ids: newValue.map((u: User) => u.id) })}
                    renderInput={(params: any) => (
                      <TextField {...params} label="Secondary Owners" placeholder="Search users…"
                        sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }} />
                    )}
                    renderOption={(props: any, option: User) => (
                      <li {...props}>
                        <Box>
                          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>{option.first_name} {option.last_name}</Typography>
                          <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, color: 'text.secondary' }}>{option.email}</Typography>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              window.setTimeout(handleCloseDialog, 0);
            }}
            sx={{ fontSize: UI_TOKENS.text.bodySm }}
          >
            Cancel
          </Button>
          {!editingApplication && setupStep > 0 && (
            <Button size="small" onClick={() => setSetupStep((step) => step - 1)}>Back</Button>
          )}
          {!editingApplication && setupStep === 1 && (
            <Button size="small" variant="contained" onClick={() => setSetupStep(2)}>Next</Button>
          )}
          {(editingApplication || setupStep === 2) && (
            <Button size="small" onClick={handleSubmit} variant="contained" disabled={loading} sx={{ fontSize: UI_TOKENS.text.bodySm }}>
              {loading ? <CircularProgress size={16} /> : (editingApplication ? 'Update' : 'Create')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Instance Dialog */}
      <Dialog open={instanceDialogOpen} onClose={handleCloseInstanceDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon sx={{ color: 'primary.main', fontSize: 18 }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
              {editingInstance ? 'Edit Instance' : (isSuperAdmin ? 'Add New Instance' : 'Request New Instance')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseInstanceDialog}><CancelIcon sx={{ fontSize: '1rem' }} /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <TextField size="small" fullWidth label="Instance Name" value={instanceFormData.name}
                onChange={(e) => setInstanceFormData({ ...instanceFormData, name: e.target.value })}
                required placeholder="e.g., Production, Staging"
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl size="small" fullWidth sx={{ '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm } }}>
                <InputLabel>Environment</InputLabel>
                <Select value={instanceFormData.environment}
                  onChange={(e) => setInstanceFormData({ ...instanceFormData, environment: e.target.value })}
                  label="Environment"
                  MenuProps={{ PaperProps: { sx: { '& .MuiMenuItem-root': { fontSize: UI_TOKENS.text.bodySm, minHeight: UI_TOKENS.menu.itemMinHeight, py: UI_TOKENS.menu.itemPaddingY } } } }}>
                  <MenuItem value="Production">Production</MenuItem>
                  <MenuItem value="Staging">Staging</MenuItem>
                  <MenuItem value="Development">Development</MenuItem>
                  <MenuItem value="UAT">UAT</MenuItem>
                  <MenuItem value="Testing">Testing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" fullWidth label="Description" value={instanceFormData.description}
                onChange={(e) => setInstanceFormData({ ...instanceFormData, description: e.target.value })}
                multiline rows={2} placeholder="Describe this instance and its purpose"
                sx={{ '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm }, '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm } }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={handleCloseInstanceDialog} sx={{ fontSize: UI_TOKENS.text.bodySm }}>Cancel</Button>
          <Button size="small" onClick={handleInstanceSubmit} variant="contained" disabled={loading} sx={{ fontSize: UI_TOKENS.text.bodySm }}>
            {loading ? <CircularProgress size={16} /> : (editingInstance ? 'Update Instance' : (isSuperAdmin ? 'Create Instance' : 'Request Instance'))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SSO Onboarding Dialog */}
      {selectedAppForSSO && (
        <SSOOnboardingDialog
          open={ssoDialogOpen}
          onClose={handleSSODialogClose}
          applicationId={selectedAppForSSO.id}
          applicationName={selectedAppForSSO.name}
          instanceId={selectedInstanceForSSO}
          riskLevel={selectedAppForSSO.risk_level}
        />
      )}

      {/* SSO Configuration View Dialog */}
      <Dialog
        open={viewSSODialogOpen}
        onClose={() => {
          setViewSSODialogOpen(false);
          setSelectedAttributes([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" fontSize="small" />
            <Typography fontWeight={700} fontSize="1rem">SSO Configuration Details</Typography>
          </Box>
          <IconButton size="small" onClick={() => { setViewSSODialogOpen(false); setSelectedAttributes([]); }}>
            ✕
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {selectedSSOConfig ? (
            <Box>
              {/* Summary chips */}
              {!isEditingSSO && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Chip label={selectedSSOConfig.application_name || 'Application'} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                    {selectedSSOConfig.instance_name && (
                      <Chip label={selectedSSOConfig.instance_name} size="small" variant="outlined" />
                    )}
                    <Chip
                      label={selectedSSOConfig.sso_type}
                      size="small"
                      sx={{
                        ...chipSxFromSurface(getProtocolSurface(selectedSSOConfig.sso_type, resolvedMode)),
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      label={selectedSSOConfig.status}
                      size="small"
                      color={getOnboardingStatusColor(selectedSSOConfig.status)}
                    />
                  </Stack>
                  <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary' }}>
                    Submitted on {new Date(selectedSSOConfig.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                  </Typography>
                </Box>
              )}

              {/* SSO Progress Stepper */}
              {!isEditingSSO && (
                <Box sx={{ px: 1, py: 1.5, mb: 1, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <SSOProgressStepper status={selectedSSOConfig.status} />
                </Box>
              )}

              {/* Status alert */}
              {['Approved', 'Completed'].includes(selectedSSOConfig.status) && (
                <Alert severity="info" sx={{ mb: 2, fontSize: UI_TOKENS.text.bodySm }}>
                  This SSO configuration has been {selectedSSOConfig.status.toLowerCase()}.
                  {selectedSSOConfig.status === 'Approved' ? ' It cannot be edited but can be viewed for reference.' : ' It cannot be edited as it has been completed.'}
                </Alert>
              )}
              {['Pending Review', 'Rejected', 'In Progress'].includes(selectedSSOConfig.status) && (
                <Alert severity="warning" sx={{ mb: 2, fontSize: UI_TOKENS.text.bodySm }}>
                  This SSO configuration is currently {selectedSSOConfig.status.toLowerCase()}. You can edit the configuration while it's in this status.
                </Alert>
              )}

              {!isEditingSSO ? (
                <SSOConfigCompactDetails
                  config={selectedSSOConfig}
                  appRiskLevel={applications.find((a: any) => a.id === selectedSSOConfig.application_id)?.risk_level}
                  attributeCatalog={availableAttributes}
                />
              ) : (
                /* ── Edit form (unchanged) ── */
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary">SSO Type</Typography>
                    <FormControl fullWidth size="small">
                      <Select value={ssoEditFormData?.sso_type || 'SAML'} onChange={(e) => handleSSOTypeChange(e.target.value)} displayEmpty>
                        <MenuItem value="SAML">SAML</MenuItem>
                        <MenuItem value="OIDC">OIDC</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary">Connection Name</Typography>
                    <TextField fullWidth size="small" value={ssoEditFormData.connection_name || ''} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, connection_name: e.target.value }))} placeholder="Connection name" InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="body2" color="text.secondary">sso-</Typography></InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary">Contact Email</Typography>
                    <TextField fullWidth size="small" value={ssoEditFormData.contact_email || ''} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, contact_email: e.target.value }))} placeholder="Contact email" />
                  </Grid>
                  {(ssoEditFormData?.sso_type || 'SAML') === 'SAML' && (
                    <>
                      <Grid item xs={12}><Typography variant="subtitle2" color="primary">Entity ID</Typography><TextField fullWidth size="small" value={ssoEditFormData.entity_id || ''} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, entity_id: e.target.value }))} placeholder="Entity ID" /></Grid>
                      <Grid item xs={12}><Typography variant="subtitle2" color="primary">ACS URL</Typography><TextField fullWidth size="small" value={ssoEditFormData.acs_url || ''} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, acs_url: e.target.value }))} placeholder="ACS URL" /></Grid>
                      <Grid item xs={12}><Typography variant="subtitle2" color="primary">Name ID Format</Typography><TextField fullWidth size="small" value={ssoEditFormData.name_id_format || ''} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, name_id_format: e.target.value }))} placeholder="Name ID Format" /></Grid>
                      <Grid item xs={12}><Typography variant="subtitle2" color="primary">X.509 Certificate</Typography><TextField fullWidth multiline rows={3} size="small" value={ssoEditFormData.x509_certificate || ''} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, x509_certificate: e.target.value }))} placeholder="X.509 Certificate" /></Grid>
                      <Grid item xs={12}><Typography variant="subtitle2" color="primary">Private Key</Typography><TextField fullWidth multiline rows={3} size="small" value={ssoEditFormData.private_key || ''} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, private_key: e.target.value }))} placeholder="Private Key" /></Grid>
                    </>
                  )}
                  {(ssoEditFormData?.sso_type || 'SAML') === 'OIDC' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="primary">Grant Type</Typography>
                        <FormControl fullWidth size="small"><Select value={ssoEditFormData?.grant_type || 'authorization_code_pkce'} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, grant_type: e.target.value }))} displayEmpty><MenuItem value="authorization_code">Authorization Code</MenuItem><MenuItem value="authorization_code_pkce">Authorization Code with PKCE</MenuItem><MenuItem value="client_credentials">Client Credentials</MenuItem></Select></FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}><Typography variant="subtitle2" color="primary">Client ID</Typography><TextField fullWidth size="small" value={ssoEditFormData.client_id || ''} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, client_id: e.target.value }))} placeholder="Client ID" /></Grid>
                      <Grid item xs={12} md={6}><Typography variant="subtitle2" color="primary">Redirect URIs</Typography><TextField fullWidth size="small" value={Array.isArray(ssoEditFormData.redirect_uris) ? ssoEditFormData.redirect_uris.join(', ') : (ssoEditFormData.redirect_uris || '')} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, redirect_uris: e.target.value.split(',').map((u: string) => u.trim()).filter((u: string) => u) }))} placeholder="Redirect URIs (comma-separated)" /></Grid>
                      <Grid item xs={12}><Typography variant="subtitle2" color="primary">Scopes</Typography><TextField fullWidth size="small" value={Array.isArray(ssoEditFormData.scopes) ? ssoEditFormData.scopes.join(', ') : (ssoEditFormData.scopes || '')} onChange={(e) => setSsoEditFormData((prev: any) => ({ ...prev, scopes: e.target.value.split(',').map((s: string) => s.trim()).filter((s: string) => s) }))} placeholder="Scopes (comma-separated)" /></Grid>
                    </>
                  )}
                  {/* Attributes to Release */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary">Attributes to Release</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select which user attributes should be released to this application during SSO authentication.</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>Standard Attributes</Typography>
                          <FormGroup>{availableAttributes.filter(attr => attr.is_standard && attr.is_active).map(attr => (<FormControlLabel key={attr.id} control={<Checkbox checked={selectedAttributes.includes(attr.id)} onChange={(e) => handleAttributeChange(attr.id, e.target.checked)} />} label={attr.display_name} />))}</FormGroup>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>Custom Attributes</Typography>
                          <FormGroup>{availableAttributes.filter(attr => !attr.is_standard && attr.is_active).map(attr => (<FormControlLabel key={attr.id} control={<Checkbox checked={selectedAttributes.includes(attr.id)} onChange={(e) => handleAttributeChange(attr.id, e.target.checked)} />} label={attr.display_name} />))}</FormGroup>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedSSOConfig && (
            <>
              {/* Only show edit button for certain statuses - not for Approved/Completed */}
              {['Pending Review', 'Rejected', 'In Progress'].includes(selectedSSOConfig.status) && !isEditingSSO && (
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={handleStartEditSSO}
                >
                  Edit Configuration
                </Button>
              )}

              {/* Show save/cancel buttons when in edit mode */}
              {isEditingSSO && (
                <>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleSaveSSOEdit}
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleCancelSSOEdit}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </>
              )}
              
              {/* Remarks & History Timeline */}


            </>
          )}
          <Button onClick={() => {
            setViewSSODialogOpen(false);
            setSelectedSSOConfig(null);
            setIsEditingSSO(false);
            setSsoEditFormData({});
            setSsoNotes([]);
            setNewNote('');
            setSelectedAttributes([]);
            // Refresh data to ensure consistency
            loadData();
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notes Timeline Dialog */}
      <Dialog 
        open={notesDialogOpen} 
        onClose={() => setNotesDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Notes & Comments Timeline</Typography>
            {selectedAppForNotes && (
              <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', mt: 0.25 }}>
                {selectedAppForNotes.name} · {selectedInstanceForNotes}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Comprehensive Timeline of Events */}
          <Box sx={{ position: 'relative', pl: 2 }}>
            {/* Timeline line */}
            <Box sx={{
              position: 'absolute',
              left: 8,
              top: 0,
              bottom: 0,
              width: 2,
              backgroundColor: 'primary.light',
              borderRadius: 1
            }} />
            
            {/* Get SSO configuration for this instance to show full timeline */}
            {(() => {
              const instanceConfig = ssoConfigurations.find(config => 
                config.application_id === selectedAppForNotes?.id && 
                config.instance_id === selectedInstanceForNotes
              );
              
              if (!instanceConfig) {
                return (
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <Box sx={{
                      position: 'absolute',
                      left: -6,
                      top: 8,
                      width: 12,
                      height: 12,
                      backgroundColor: 'grey.400',
                      borderRadius: '50%',
                      border: '2px solid white',
                      boxShadow: 1
                    }} />
                    <Paper sx={{ p: 2, ml: 2, backgroundColor: 'grey.50' }}>
                      <Typography variant="body2" color="text.secondary">
                        No SSO configuration found for this instance.
                      </Typography>
                    </Paper>
                  </Box>
                );
              }

              return (
                <>
                  {/* Configuration Created Event */}
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <Box sx={{
                      position: 'absolute',
                      left: -6,
                      top: 8,
                      width: 12,
                      height: 12,
                      backgroundColor: 'primary.main',
                      borderRadius: '50%',
                      border: '2px solid white',
                      boxShadow: 1
                    }} />
                    <Paper sx={{ p: 2, ml: 2, backgroundColor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                          SSO Configuration Created
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(instanceConfig.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Initial SSO configuration submitted for {instanceConfig.application_name}
                      </Typography>
                      <Chip 
                        label="Created" 
                        size="small" 
                        color="primary" 
                        sx={{ mt: 1 }}
                      />
                    </Paper>
                  </Box>

                  {/* Status Change Events */}
                  {instanceConfig.reviewed_at && (
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Box sx={{
                        position: 'absolute',
                        left: -6,
                        top: 8,
                        width: 12,
                        height: 12,
                        backgroundColor: getOnboardingStatusColor(instanceConfig.status) === 'success' ? '#4caf50' : 
                                       getOnboardingStatusColor(instanceConfig.status) === 'error' ? '#f44336' : '#ff9800',
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: 1
                      }} />
                      <Paper sx={{ p: 2, ml: 2, backgroundColor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                            Status Updated to {instanceConfig.status}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(instanceConfig.reviewed_at).toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          SSO configuration status changed by SSO admin
                        </Typography>
                        {instanceConfig.review_notes && (
                          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                            "{instanceConfig.review_notes}"
                          </Typography>
                        )}
                        <Chip 
                          label={instanceConfig.status} 
                          size="small" 
                          color={getOnboardingStatusColor(instanceConfig.status)}
                          sx={{ mt: 1 }}
                        />
                      </Paper>
                    </Box>
                  )}

                  {/* Assignment Event */}
                  {instanceConfig.assigned_to_name && (
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Box sx={{
                        position: 'absolute',
                        left: -6,
                        top: 8,
                        width: 12,
                        height: 12,
                        backgroundColor: '#2196f3',
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: 1
                      }} />
                      <Paper sx={{ p: 2, ml: 2, backgroundColor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                            Assigned to SSO Admin
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(instanceConfig.updated_at).toLocaleString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Configuration assigned to <strong>{instanceConfig.assigned_to_name}</strong>
                        </Typography>
                        <Chip 
                          label="Assignment" 
                          size="small" 
                          color="info" 
                          sx={{ mt: 1 }}
                        />
                      </Paper>
                    </Box>
                  )}

                  {/* Notes/Remarks Events */}
                  {instanceNotes.length > 0 ? (
                    instanceNotes.map((note, index) => (
                      <Box key={index} sx={{ position: 'relative', mb: 2 }}>
                        <Box sx={{
                          position: 'absolute',
                          left: -6,
                          top: 8,
                          width: 12,
                          height: 12,
                          backgroundColor: '#9c27b0',
                          borderRadius: '50%',
                          border: '2px solid white',
                          boxShadow: 1
                        }} />
                        <Paper sx={{ p: 2, ml: 2, backgroundColor: 'secondary.50', border: '1px solid', borderColor: 'secondary.200' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
                              {note.author_name || 'Unknown User'} added a {note.note_type || 'comment'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(note.created_at).toLocaleString()}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {note.content}
                          </Typography>
                          {note.note_type && (
                            <Chip 
                              label={note.note_type} 
                              size="small" 
                              color="secondary" 
                              sx={{ mt: 1 }}
                            />
                          )}
                          {note.is_internal && (
                            <Chip 
                              label="Internal" 
                              size="small" 
                              color="warning" 
                              sx={{ mt: 1, ml: 1 }}
                            />
                          )}
                        </Paper>
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ position: 'relative', mb: 2 }}>
                      <Box sx={{
                        position: 'absolute',
                        left: -6,
                        top: 8,
                        width: 12,
                        height: 12,
                        backgroundColor: 'grey.400',
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: 1
                      }} />
                      <Paper sx={{ p: 2, ml: 2, backgroundColor: 'grey.50' }}>
                        <Typography variant="body2" color="text.secondary">
                          No additional notes or comments available.
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </>
              );
            })()}
          </Box>

          {/* Add new note section */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Add Note/Comment
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Add a note or comment..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleAddNoteToInstance}
                disabled={!newNote.trim()}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add Note
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon sx={{ color: 'success.main', fontSize: 18 }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>Import Applications from CSV</Typography>
          </Box>
          <IconButton size="small" onClick={() => setImportDialogOpen(false)}><CancelIcon sx={{ fontSize: '1rem' }} /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a CSV file with application data. The CSV should include columns like:
              Application Name, Description, Business Function, Risk Level, Status.
            </Typography>
            
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="csv-upload"
            />
            <label htmlFor="csv-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Choose CSV File
              </Button>
            </label>
            
            {importFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {importFile.name}
              </Typography>
            )}
          </Box>

          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importError}
            </Alert>
          )}

          {importPreview.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Preview ({importPreview.length} applications)
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Application Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Business Function</TableCell>
                      <TableCell>Risk Level</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importPreview.slice(0, 10).map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row['Application Name'] || row['Name'] || ''}</TableCell>
                        <TableCell>{row['Description'] || ''}</TableCell>
                        <TableCell>{row['Business Function'] || ''}</TableCell>
                        <TableCell>{row['Risk Level'] || 'Low'}</TableCell>
                        <TableCell>{row['Status'] || 'Active'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {importPreview.length > 10 && (
                <Typography variant="caption" color="text.secondary">
                  Showing first 10 rows of {importPreview.length} total
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleImport} 
            variant="contained" 
            disabled={!importPreview.length || importLoading}
          >
            {importLoading ? 'Importing...' : 'Import Applications'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Owners Dialog */}
      {selectedAppForOwners && (
        <OwnersDialog
          open={ownersDialogOpen}
          onClose={handleCloseOwners}
          application={selectedAppForOwners}
        />
      )}

      {/* SSO Details Small Dialog */}
      {selectedAppForSSODetails && (
        <SSODetailsSmallDialog
          open={ssoDetailsSmallDialogOpen}
          onClose={handleCloseSSODetails}
          application={selectedAppForSSODetails}
        />
      )}

      {oidcCredentials && (
        <OIDCCredentialsDialog
          open={oidcDialogOpen}
          onClose={() => {
            setOidcDialogOpen(false);
            setOidcCredentials(null);
          }}
          credentials={oidcCredentials}
        />
      )}

      {/* Delegation Dialog - Compact Version - DEBUG: This is the Instance Delegation Dialog */}
      <Dialog open={delegationDialogOpen} onClose={handleCloseDelegationDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 600,
          fontSize: '1.1rem'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon />
            Delegate Instance Onboarding
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedAppForDelegation && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {selectedAppForDelegation.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedAppForDelegation.description}
              </Typography>
            </Box>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Delegate To</InputLabel>
                <Select
                  value={delegationFormData.delegateTo}
                  onChange={(e) => setDelegationFormData({ ...delegationFormData, delegateTo: e.target.value })}
                  label="Delegate To"
                >
                  {users.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No users available. Please contact your administrator.
                      </Typography>
                    </MenuItem>
                  ) : (
                    users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            width: 28, 
                            height: 28, 
                            borderRadius: '50%', 
                            backgroundColor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: UI_TOKENS.text.caption
                          }}>
                            {`${(user.first_name || '').trim().charAt(0)}${(user.last_name || '').trim().charAt(0)}`.toUpperCase() || 'U'}
                          </Box>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Business Justification"
                value={delegationFormData.businessJustification}
                onChange={(e) => setDelegationFormData({ ...delegationFormData, businessJustification: e.target.value })}
                placeholder="Explain the business value and context for this delegation..."
                helperText="Provide business justification for delegating this task"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Button 
            onClick={handleCloseDelegationDialog}
            sx={{ 
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'grey.100' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelegationSubmit} 
            variant="contained" 
            disabled={loading || !delegationFormData.delegateTo || !delegationFormData.businessJustification}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
              },
              transition: 'all 0.2s ease-in-out',
              fontWeight: 600,
              px: 3
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Delegate Onboarding'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reports Dialog */}
      <Dialog open={reportsDialogOpen} onClose={handleCloseReportsDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <AssessmentIcon />
          User Activity Reports
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {reportLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Inactive Users Report */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#1976d2', fontWeight: 600 }}>
                  Inactive Users Report
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Users (Primary/Secondary Owners and Delegates) who are currently inactive
                </Typography>
                
                {inactiveUsersReport.length === 0 ? (
                  <Box sx={{ 
                    p: 3, 
                    textAlign: 'center', 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="body1" color="text.secondary">
                      No inactive users found
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Application</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>User Type</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Last Login</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Instance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inactiveUsersReport.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{item.applicationName}</TableCell>
                            <TableCell>
                              <Chip 
                                label={item.userType} 
                                size="small" 
                                color={item.userType === 'Primary Owner' ? 'primary' : item.userType === 'Secondary Owner' ? 'secondary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{item.userName}</TableCell>
                            <TableCell>{item.userEmail}</TableCell>
                            <TableCell>{item.lastLogin}</TableCell>
                            <TableCell>{item.instanceName || 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* All Inactive Report */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2, color: '#d32f2f', fontWeight: 600 }}>
                  Applications with All Inactive Users
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Applications where all primary owners, secondary owners, and delegates are inactive
                </Typography>
                
                {allInactiveReport.length === 0 ? (
                  <Box sx={{ 
                    p: 3, 
                    textAlign: 'center', 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="body1" color="text.secondary">
                      No applications with all inactive users found
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Application</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Total Users</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Inactive Users</TableCell>
                          <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Users Details</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allInactiveReport.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {item.applicationName}
                              </Typography>
                            </TableCell>
                            <TableCell>{item.totalUsers}</TableCell>
                            <TableCell>
                              <Chip 
                                label={item.inactiveUsers} 
                                color="error" 
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box>
                                {item.users.map((user: any, userIndex: number) => (
                                  <Box key={userIndex} sx={{ mb: 1, p: 1, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                      {user.userType}: {user.userName}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption" color="text.secondary">
                                      {user.userEmail} • Last Login: {user.lastLogin}
                                    </Typography>
                                    {user.instanceName !== 'N/A' && (
                                      <>
                                        <br />
                                        <Typography variant="caption" color="text.secondary">
                                          Instance: {user.instanceName}
                                        </Typography>
                                      </>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Button onClick={handleCloseReportsDialog} variant="outlined">
            Close
          </Button>
          <Button 
            onClick={handleOpenReportsDialog} 
            variant="contained" 
            startIcon={<AssessmentIcon />}
            disabled={reportLoading}
          >
            Refresh Reports
          </Button>
        </DialogActions>
      </Dialog>

      {/* Application Delegates Management Dialog */}
      <Dialog
        open={delegatesDialogOpen}
        onClose={handleCloseDelegatesDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.primary} 100%)`,
            color: colors.contrastText,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1.75,
          }}
        >
          <PeopleIcon />
          Manage Application Delegates
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: 'background.paper' }}>
          {selectedAppForDelegates && (
            <Box>
              <Box
                sx={{
                  mb: 2.5,
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {selectedAppForDelegates.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Application delegates
                </Typography>
              </Box>

              {/* Current Delegates List */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Current Delegates ({applicationDelegates.length})
                </Typography>
                {applicationDelegates.length === 0 ? (
                  <Box
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderRadius: 1.5,
                      border: '1px dashed',
                      borderColor: 'divider',
                      bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No delegates assigned to this application.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxHeight: 300, bgcolor: 'background.paper' }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50' }}>
                            Name
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50' }}>
                            Email
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50' }}>
                            Role
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50' }}>
                            Ends
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50' }}>
                            Scope
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50' }}>
                            Status
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50' }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {applicationDelegates.map((delegate) => (
                          <TableRow key={delegate.id} hover>
                            <TableCell>
                              {delegate.first_name} {delegate.last_name}
                            </TableCell>
                            <TableCell>{delegate.email}</TableCell>
                            <TableCell>{delegate.role}</TableCell>
                            <TableCell>
                              {delegate.ends_at ? calendarDay(delegate.ends_at) : '—'}
                            </TableCell>
                            <TableCell>
                              {(delegate.scope || 'view,submit_sso').replace('submit_sso', 'submit SSO')}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={delegationIsCurrent(delegate, calendarDay(new Date())) ? delegate.status : 'Expired'}
                                color={delegationIsCurrent(delegate, calendarDay(new Date())) && delegate.status === 'active' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleRemoveDelegate(delegate.id)}
                                disabled={loading}
                              >
                                Remove
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* Add New Delegate Form */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Add New Delegate
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={users.filter(u => !applicationDelegates.some(d => d.user_id === u.id))}
                    getOptionLabel={(option: User) => `${option.first_name} ${option.last_name} (${option.email})`}
                    value={users.filter(u => newDelegateFormData.userIds.includes(u.id))}
                    onChange={(event: any, newValue: User[]) => {
                      setNewDelegateFormData({
                        ...newDelegateFormData,
                        userIds: newValue.map(user => user.id),
                      });
                    }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        label="Select Users"
                        placeholder="Search and select users..."
                        required
                        helperText="You can select multiple users to delegate to"
                      />
                    )}
                    renderOption={(props: any, option: User) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2">
                            {option.first_name} {option.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email} • {option.role}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderTags={(value: User[], getTagProps: any) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={`${option.first_name} ${option.last_name}`}
                          variant="outlined"
                          size="small"
                          color="primary"
                        />
                      ))
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Business Justification"
                    multiline
                    rows={3}
                    value={newDelegateFormData.businessJustification}
                    onChange={(e) =>
                      setNewDelegateFormData({
                        ...newDelegateFormData,
                        businessJustification: e.target.value,
                      })
                    }
                    placeholder="Explain the business value and context for this delegation..."
                    helperText="Provide business justification for delegating this application"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Start"
                    InputLabelProps={{ shrink: true }}
                    value={newDelegateFormData.startsAt}
                    onChange={(e) => setNewDelegateFormData({ ...newDelegateFormData, startsAt: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="End"
                    InputLabelProps={{ shrink: true }}
                    value={newDelegateFormData.endsAt}
                    onChange={(e) => setNewDelegateFormData({ ...newDelegateFormData, endsAt: e.target.value })}
                    helperText="Extra actions stop after this date"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormGroup row>
                    {DELEGATION_SCOPES.map((scope) => (
                      <FormControlLabel
                        key={scope}
                        control={
                          <Checkbox
                            checked={newDelegateFormData.scopes.includes(scope)}
                            onChange={(event) => {
                              const scopes = event.target.checked
                                ? [...newDelegateFormData.scopes, scope]
                                : newDelegateFormData.scopes.filter((item) => item !== scope);
                              setNewDelegateFormData({ ...newDelegateFormData, scopes });
                            }}
                          />
                        }
                        label={scope === 'submit_sso' ? 'Submit SSO' : scope === 'edit' ? 'Edit' : 'View'}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: resolvedMode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
          }}
        >
          <Button onClick={handleCloseDelegatesDialog} variant="outlined" color="primary">
            Close
          </Button>
          <Button
            onClick={handleAddDelegate}
            variant="contained"
            color="primary"
            disabled={
              loading ||
              Boolean(delegationValidationError({
                userId: newDelegateFormData.userIds[0],
                reason: newDelegateFormData.businessJustification,
                startsAt: newDelegateFormData.startsAt,
                endsAt: newDelegateFormData.endsAt,
                scopes: newDelegateFormData.scopes,
              }))
            }
            sx={{
              fontWeight: 600,
              px: 2.5,
              background: `linear-gradient(135deg, ${colors.dark} 0%, ${colors.primary} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
              },
              '&.Mui-disabled': {
                background: resolvedMode === 'dark' ? 'rgba(255,255,255,0.12)' : undefined,
              },
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Add Delegate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Instance Creation Dialog */}
      <InstanceEditorDialog
        open={instanceDialogOpen}
        loading={loading}
        isEditing={Boolean(editingInstance)}
        selectedAppName={selectedAppForInstance?.name}
        formData={instanceFormData}
        onFormDataChange={setInstanceFormData}
        onClose={handleCloseInstanceDialog}
        onSubmit={handleInstanceSubmit}
      />

      {/* Import IDP Dialog */}
      <Dialog
        open={importIdpDialogOpen}
        onClose={() => { setImportIdpDialogOpen(false); setImportIdpError(null); }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
            Import Applications from Identity Provider
          </Typography>
          {availableApplications.length > 0 && (
            <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', mt: 0.5 }}>
              {availableApplications.length} available · {selectedApplications.length} selected
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box>
            {importIdpError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setImportIdpError(null)}
                action={
                  configuredImportIdps.length === 0 ? (
                    <Button color="inherit" size="small" onClick={() => navigate('/security')}>
                      Security
                    </Button>
                  ) : undefined
                }
              >
                {importIdpError}
              </Alert>
            )}
            {configuredImportIdps.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', mb: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 280, flex: 1 }} size="small">
                <InputLabel>Identity Provider</InputLabel>
                <Select
                  value={selectedIdp}
                  label="Identity Provider"
                  onChange={(e) => { void handleIdpSourceChange(e.target.value); }}
                >
                  {configuredImportIdps.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedIdp && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => { void loadIdpApplications(selectedIdp); }}
                  disabled={importIdpLoading}
                  startIcon={importIdpLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                >
                  {importIdpLoading ? 'Loading…' : 'Refresh'}
                </Button>
              )}
            </Box>
            ) : (
              !importIdpLoading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Configure at least one identity provider in{' '}
                  <Button size="small" onClick={() => navigate('/security')}>Security settings</Button>
                  {' '}to import applications.
                </Alert>
              )
            )}

            {selectedIdp && configuredImportIdps.length > 0 && availableApplications.length > 0 && (
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: UI_TOKENS.text.bodySm, mb: 1 }}>
                  Select applications to import
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, borderRadius: 2 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ bgcolor: 'grey.50' }}>
                          <Checkbox
                            indeterminate={selectedApplications.length > 0 && selectedApplications.length < availableApplications.length}
                            checked={availableApplications.length > 0 && selectedApplications.length === availableApplications.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedApplications(availableApplications.map((app) => app.id));
                              } else {
                                setSelectedApplications([]);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 700, fontSize: UI_TOKENS.text.helper }}>Application Name</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 700, fontSize: UI_TOKENS.text.helper }}>Type</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 700, fontSize: UI_TOKENS.text.helper }}>Owners</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.50', fontWeight: 700, fontSize: UI_TOKENS.text.helper }}>Owner Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {availableApplications.map((app) => (
                        <TableRow
                          key={app.id}
                          hover
                          sx={getImportRowSx(app)}
                          onClick={() => toggleImportAppSelection(app.id, !selectedApplications.includes(app.id))}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedApplications.includes(app.id)}
                              onChange={(e) => toggleImportAppSelection(app.id, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>
                              {app.displayName || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={detectSSOType(app)}
                              size="small"
                              variant="outlined"
                              color={detectSSOType(app) === 'SAML' ? 'primary' : 'secondary'}
                              sx={{ height: 22, fontSize: UI_TOKENS.text.helper }}
                            />
                          </TableCell>
                          <TableCell>
                            {(app.ownerEmails?.length ?? 0) === 0 ? (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: UI_TOKENS.text.caption }}>
                                No owners in IdP
                              </Typography>
                            ) : (
                              <Box>
                                {app.ownerEmails?.map((email: string, idx: number) => (
                                  <Typography key={idx} variant="body2" sx={{ fontSize: UI_TOKENS.text.caption }}>
                                    {email}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            {(app.ownerEmails?.length ?? 0) === 0 ? (
                              <Chip label="No owners" size="small" color="default" />
                            ) : (
                              <Chip
                                label={app.allOwnersExistInLocalDB ? 'All Found' : 'Missing Owners'}
                                size="small"
                                color={app.allOwnersExistInLocalDB ? 'success' : 'error'}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {selectedIdp && configuredImportIdps.length > 0 && availableApplications.length === 0 && !importIdpLoading && !importIdpError && (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No applications loaded yet. Click Refresh to fetch from Entra ID.
                </Typography>
              </Box>
            )}

            {importIdpLoading && availableApplications.length === 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setImportIdpDialogOpen(false); setImportIdpError(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImportApplications}
            disabled={selectedApplications.length === 0 || importIdpLoading}
            startIcon={importIdpLoading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
          >
            {importIdpLoading
              ? 'Importing…'
              : `Import ${selectedApplications.length} Application${selectedApplications.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Risk Assessment Dialog ──────────────────────────────────── */}
      <Dialog open={raDialogOpen} onClose={() => setRaDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            <Box>
              <Typography variant="h6">Risk Assessment — {raApp?.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                Answer all questions so GRC Admin can determine the risk level and required controls
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {raLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Error Alert */}
              {raError && (
                <Alert severity="error" sx={{ m: 2 }} onClose={() => setRaError(null)}>
                  {raError}
                </Alert>
              )}

              {/* GRC notes when Needs More Information */}
              {raApp && appAssessments[raApp.id]?.status === 'Needs More Information' && (
                <Alert severity="warning" sx={{ m: 2 }} icon={<RateReviewIcon />}>
                  <Typography variant="subtitle2">GRC Admin needs more information:</Typography>
                  <Typography variant="body2">{appAssessments[raApp.id]?.review_notes || 'Please review and update your answers.'}</Typography>
                </Alert>
              )}

              {raControlPreview && (
                <Alert severity="info" sx={{ m: 2 }}>
                  <Typography variant="subtitle2">
                    {raControlPreview.score == null
                      ? `Control before submit: ${raControlPreview.name}`
                      : `Score ${raControlPreview.score}. Control before submit: ${raControlPreview.name}`}
                  </Typography>
                  <Typography variant="body2">{raControlPreview.sentence}</Typography>
                </Alert>
              )}

              {/* Progress bar */}
              <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Completion</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(() => {
                      const cats = Array.from(new Set(raQuestions.map((q: any) => q.category as string)));
                      const section = cats.length ? `Section ${Math.min(raActiveTab + 1, cats.length)} of ${cats.length}. ` : '';
                      const percent = raQuestions.length > 0 ? Math.round((Object.keys(raAnswers).length / raQuestions.length) * 100) : 0;
                      return `${section}${percent}%`;
                    })()}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={raQuestions.length > 0 ? Math.round((Object.keys(raAnswers).length / raQuestions.length) * 100) : 0}
                  sx={{ height: 6, borderRadius: 1 }}
                />
              </Box>

              {/* Category Tabs */}
              {(() => {
                const cats = Array.from(new Set(raQuestions.map((q: any) => q.category as string)));
                const catQuestions = (cat: string) => raQuestions.filter((q: any) => q.category === cat);
                return (
                  <>
                    <Tabs
                      value={raActiveTab}
                      onChange={(_, v) => setRaActiveTab(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
                    >
                      {cats.map((cat, i) => (
                        <Tab key={cat} label={`${cat} (${catQuestions(cat).length})`} value={i} sx={{ fontSize: UI_TOKENS.text.caption }} />
                      ))}
                    </Tabs>

                    {cats.map((cat, catIdx) => (
                      <Box key={cat} sx={{ display: raActiveTab === catIdx ? 'block' : 'none', p: 2 }}>
                        <Grid container spacing={2}>
                          {catQuestions(cat).map((q: any) => (
                            <Grid item xs={12} md={6} key={q.id}>
                              <Card variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                  Q{q.question_number}: {q.question_text}
                                  {q.is_required && <Chip label="Required" size="small" color="error" sx={{ ml: 1, fontSize: '0.6rem', height: 16 }} />}
                                </Typography>
                                {q.help_text && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                    {q.help_text}
                                  </Typography>
                                )}

                                {/* Yes/No */}
                                {q.response_type === 'yes_no' && (
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={raAnswers[q.id] ?? ''}
                                      onChange={e => setRaAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                                      displayEmpty
                                    >
                                      <MenuItem value="" disabled><em>Select...</em></MenuItem>
                                      <MenuItem value="true">Yes</MenuItem>
                                      <MenuItem value="false">No</MenuItem>
                                    </Select>
                                  </FormControl>
                                )}

                                {/* Dropdown */}
                                {q.response_type === 'dropdown' && (
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={raAnswers[q.id] ?? ''}
                                      onChange={e => setRaAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                                      displayEmpty
                                    >
                                      <MenuItem value="" disabled><em>Select...</em></MenuItem>
                                      {(Array.isArray(q.response_options)
                                        ? q.response_options
                                        : JSON.parse(q.response_options || '[]')
                                      ).map((opt: any) => {
                                        const val = typeof opt === 'object' ? opt.value : opt;
                                        const lbl = typeof opt === 'object' ? opt.label : opt;
                                        return <MenuItem key={val} value={val}>{lbl}</MenuItem>;
                                      })}
                                    </Select>
                                  </FormControl>
                                )}

                                {/* Multi-select */}
                                {q.response_type === 'multi_select' && (
                                  <FormGroup>
                                    {(Array.isArray(q.response_options)
                                      ? q.response_options
                                      : JSON.parse(q.response_options || '[]')
                                    ).map((opt: any) => {
                                      const val = typeof opt === 'object' ? opt.value : opt;
                                      const lbl = typeof opt === 'object' ? opt.label : opt;
                                      return (
                                      <FormControlLabel
                                        key={val}
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={(raAnswers[q.id] || []).includes(val)}
                                            onChange={e => {
                                              const current: string[] = raAnswers[q.id] || [];
                                              setRaAnswers(prev => ({
                                                ...prev,
                                                [q.id]: e.target.checked ? [...current, val] : current.filter((v: string) => v !== val)
                                              }));
                                            }}
                                          />
                                        }
                                        label={<Typography variant="body2">{lbl}</Typography>}
                                      />
                                      );
                                    })}
                                  </FormGroup>
                                )}

                                {/* Numeric */}
                                {q.response_type === 'numeric' && (
                                  <TextField
                                    fullWidth size="small" type="number"
                                    value={raAnswers[q.id] ?? ''}
                                    onChange={e => setRaAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                                    inputProps={{ min: 0 }}
                                  />
                                )}

                                {/* Text */}
                                {q.response_type === 'text' && (
                                  <TextField
                                    fullWidth size="small" multiline rows={2}
                                    value={raAnswers[q.id] ?? ''}
                                    onChange={e => setRaAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                                  />
                                )}

                                {q.notes && (
                                  <Typography variant="caption" color="info.main" display="block" sx={{ mt: 0.5 }}>
                                    ℹ️ {q.notes}
                                  </Typography>
                                )}
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    ))}
                  </>
                );
              })()}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setRaDialogOpen(false)}>{raAssessmentStatus === 'Approved' ? 'Close' : 'Cancel'}</Button>
          {raAssessmentStatus === 'Approved' ? (
            <Alert severity="success" sx={{ flex: 1, py: UI_TOKENS.menu.itemPaddingY, fontSize: UI_TOKENS.text.bodySm }}>
              This assessment has been approved. Contact your GRC admin to initiate a re-review.
            </Alert>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleSaveRiskAnswers}
                disabled={raSubmitting || raLoading || !raAssessmentId}
                startIcon={raSubmitting ? <CircularProgress size={14} /> : <AssessmentIcon />}
              >
                Save Progress
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmitRiskAssessment}
                disabled={raSubmitting || raLoading || !raAssessmentId}
                startIcon={raSubmitting ? <CircularProgress size={14} /> : <SendIcon />}
              >
                Submit for GRC Review
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Applications;

